// Phase137 Line filter
// Phase127 snapshot request never hides Weekly report
// Phase126 Weekly month snapshot cutoff labels
// Phase123 display name: Subchannel -> Subchannel
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import CheckboxDropdown from './CheckboxDropdown';
import AiInsightPanel, { askInsight, INSIGHTS_ON, INSIGHTS_OFF_MARK } from './AiInsightPanel';
import useAuth from './useAuth';
import { BizInventory, BizMarketing, BIZ_PAGES, BP_TABLES_ON, BP_TABLES_OFF_MARK } from './BizTables';
import { BizPole } from './BizPole';
import './SalesReportDashboard.css';
import {
  API_BASE,
  WEEKLY2_INACTIVE_FILTERS,
  apiReportType,
  buildExportFileName,
  fetchSalesReportJson,
  fetchSalesReportVisualHistory,
  formatMoney,
  formatNumber,
  formatPercent,
  GrowthCell,
  isAbortError,
  isMonthlyReportType,
  isWeeklyReport2Type,
  isWeeklyReportType,
  monthNames,
  normalizeSalesReportFilterMeta,
  normalizeWeekly2Filters,
  rememberSalesReport,
  salesReportCacheKey,
  salesReportRequestBody,
  selectedText,
  StatusCell,
  TargetCell,
} from './salesReportShared';
import {
  weekly2DisplayRows,
  weekly2ElapsedQuarterDays,
  weekly2MonthForWeek,
  weekly2Number,
  weekly2PreviousQuarterTotal,
  weekly2QuarterDayCount,
  weekly2QuarterGroups,
  weekly2QuarterOptions,
  weekly2QuarterTotal,
  weekly2Ratio,
  weekly2Section,
  weekly2WeekKey,
  weekly2WeekValue,
  weekly2YearTotal,
} from './salesReportWeekly2Utils';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie, Sector,
  ReferenceLine, LabelList,
} from 'recharts';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';

// Phase122 hide target columns for detailed filters
const salesReportHasDetailedTargetlessFilter = (filters = {}) => (
  (filters.customerNames || []).length > 0
  || (filters.skus || []).length > 0
    || (filters.lines || []).length > 0
  || (filters.regions || []).length > 0
  || (filters.subregions || []).length > 0
  || (filters.countries || []).length > 0
);

const salesReportVisibleColumns = (columns = [], filters = {}) => (
  salesReportHasDetailedTargetlessFilter(filters)
    // Phase136 hide Weekly snapshot for detailed filters
    ? columns.filter((column) => (
      column.group !== 'target'
      && column.group !== 'monthProjection'
      && column.group !== 'ytdTarget'
      && column.group !== 'yearTarget'
      && !String(column?.key || '').startsWith('monthSnapshot')
    ))
    : columns
);

// Phase125 Weekly month snapshot
const phase125NormalizeDimension = (value) => String(value ?? '').trim().toLowerCase();

const phase125MergeWeeklyMonthSnapshot = (weeklyReport, monthlyReport) => {
  if (!weeklyReport || !monthlyReport) return weeklyReport;
  const monthlySections = new Map(
    (monthlyReport.sections || []).map((section) => [
      String(section?.key || section?.label || '').trim().toLowerCase(),
      section,
    ]),
  );

  return {
    ...weeklyReport,
    meta: {
      ...(weeklyReport.meta || {}),
      monthSnapshotMonth: monthlyReport.meta?.month,
      monthSnapshotYear: monthlyReport.meta?.year,
      monthSnapshotEndDate: monthlyReport.meta?.snapshotEndDate,
      monthSnapshotEndLabel: monthlyReport.meta?.snapshotEndDate || `Month ${monthlyReport.meta?.month}`,
    },
    sections: (weeklyReport.sections || []).map((section) => {
      const sectionKey = String(section?.key || section?.label || '').trim().toLowerCase();
      const monthlySection = monthlySections.get(sectionKey);
      const monthlyRows = new Map(
        (monthlySection?.rows || []).map((row) => [phase125NormalizeDimension(row?.dimensionValue), row]),
      );

      return {
        ...section,
        rows: (section.rows || []).map((row) => {
          const monthlyRow = monthlyRows.get(phase125NormalizeDimension(row?.dimensionValue));
          return {
            ...row,
            monthSnapshotAmount: monthlyRow?.amountCurrent ?? null,
            monthSnapshotTarget: monthlyRow?.target ?? null,
            monthSnapshotAchieved: (monthlyRow?.target && monthlyRow?.amountCurrent != null)
              ? (monthlyRow.amountCurrent / monthlyRow.target)
              : (monthlyRow?.achieved ?? null),
            // Phase134 Weekly snapshot uses one cutoff day
            monthSnapshotDayCount: monthlyReport.meta?.snapshotDayCount ?? monthlyRow?.dayCount ?? null,
            monthSnapshotProjected: monthlyRow?.projected ?? null,
            monthSnapshotStatus: monthlyRow?.status ?? null,
          };
        }),
      };
    }),
  };
};

// Phase131 compact Weekly snapshot headers
const phase131SnapshotDateLabel = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value || '');
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// Phase132 Weekly snapshot section visibility
const phase132WeeklySnapshotSectionKeys = new Set([
  'entity',
  'entity 3',
  'entity3',
  'channel',
  'subchannel',
  'channel2',
  'channel 3',
  'channel3',
]);

const phase132ColumnsForSection = (columns = [], section = {}) => {
  const sectionKey = String(section?.key || section?.label || '').trim().toLowerCase();
  if (phase132WeeklySnapshotSectionKeys.has(sectionKey)) return columns;
  return columns.filter((column) => {
    const key = String(column?.key || '');
    return !key.startsWith('monthSnapshot')
      && key !== 'yearYtdStatus'
      && key !== 'yearProjectedAchieved'
      && key !== 'yearProjectedStatus';
  });
};

// Phase135 hide Weekly snapshot columns from PDF
const phase135PdfColumns = (columns = []) => (
  columns.filter((column) => !String(column?.key || '').startsWith('monthSnapshot'))
);

// Phase140 final-response HEMA target fallback
const phase140Number = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const phase140EntityName = (row) => String(row?.dimensionValue || '').trim().toUpperCase();

const phase140TargetMetrics = (row, target, reportType) => {
  const numerator = phase140Number(row?.amountCurrent);
  const projected = phase140Number(row?.projected);
  return {
    ...row,
    target,
    achieved: target > 0 ? numerator / target : null,
    status: target > 0 ? (numerator >= target ? 'Pass' : 'Fail') : '-',
    projectedAchieved: reportType === 'monthly' && target > 0 ? projected / target : row?.projectedAchieved,
    projectedStatus: reportType === 'monthly' && target > 0 ? (projected >= target ? 'Pass' : 'Fail') : row?.projectedStatus,
  };
};

const phase140ApplyHemaTargetFallback = (report) => {
  if (!report || !Array.isArray(report.sections)) return report;
  const reportType = String(report?.meta?.reportType || '').toLowerCase();
  if (reportType !== 'weekly' && reportType !== 'monthly') return report;

  return {
    ...report,
    sections: report.sections.map((section) => {
      const key = String(section?.key || section?.label || '').trim().toLowerCase();
      if (!['entity', 'entity3', 'entity 3'].includes(key) || !Array.isArray(section.rows)) {
        return section;
      }

      const hema = section.rows.find((row) => phase140EntityName(row) === 'HEMA');
      const east = section.rows.find((row) => phase140EntityName(row) === 'HEMA_EAST');
      const west = section.rows.find((row) => phase140EntityName(row) === 'HEMA_WEST');
      const directTarget = phase140Number(hema?.target);
      const childTarget = phase140Number(east?.target) + phase140Number(west?.target);

      let rows = section.rows;
      if (hema && directTarget <= 0 && childTarget > 0) {
        rows = rows.map((row) => (
          phase140EntityName(row) === 'HEMA'
            ? phase140TargetMetrics(row, childTarget, reportType)
            : row
        ));
      }

      const hasHemaParent = rows.some((row) => phase140EntityName(row) === 'HEMA');
      const totalTarget = rows
        .filter((row) => {
          const name = phase140EntityName(row);
          return name !== 'TOTAL'
            && String(row?.rowType || '').toLowerCase() !== 'total'
            && !(hasHemaParent && (name === 'HEMA_EAST' || name === 'HEMA_WEST'));
        })
        .reduce((sum, row) => sum + phase140Number(row?.target), 0);

      return {
        ...section,
        rows: rows.map((row) => {
          const name = phase140EntityName(row);
          const isTotal = name === 'TOTAL' || String(row?.rowType || '').toLowerCase() === 'total';
          return isTotal ? phase140TargetMetrics(row, totalTarget, reportType) : row;
        }),
      };
    }),
  };
};

// Phase148 clarify yearly target metrics on Monthly Page
const phase148AddYearProjectedAchievement = (report) => {
  if (!report || !Array.isArray(report.sections)) return report;
  if (!isMonthlyReportType(report?.meta?.reportType)) return report;

  const yearlyProjectedSections = new Set([
    'entity',
    'entity3',
    'entity 3',
    'channel',
    'subchannel',
    'channel2',
    'channel 2',
  ]);
  const reportYearDayCount = report.sections
    .filter((section) => yearlyProjectedSections.has(String(section?.key || section?.label || '').trim().toLowerCase()))
    .flatMap((section) => section.rows || [])
    .reduce((max, row) => Math.max(max, phase140Number(row?.yearDayCount)), 0);

  return {
    ...report,
    sections: report.sections.map((section) => {
      const rows = section.rows || [];
      const sectionKey = String(section?.key || section?.label || '').trim().toLowerCase();
      const hemaChildYearDayCount = sectionKey === 'entity'
        ? rows
            .filter((row) => ['HEMA_EAST', 'HEMA_WEST'].includes(phase140EntityName(row)))
            .reduce((max, row) => Math.max(max, phase140Number(row?.yearDayCount)), 0)
        : 0;

      return {
        ...section,
        rows: rows.map((row) => {
          const monthlyTarget = phase140Number(row?.target);
          const monthlyAmount = phase140Number(row?.amountCurrent);
          const monthlyProjected = phase140Number(row?.projected);
          const yearTarget = phase140Number(row?.yearTarget);
          const ytdAmount = phase140Number(row?.ytdAmountCurrent);
          const rowYearDayCount = phase140Number(row?.yearDayCount);
          const sharedYearDayCount = yearlyProjectedSections.has(sectionKey) && reportYearDayCount > rowYearDayCount
            ? reportYearDayCount
            : rowYearDayCount;
          const adjustedYearDayCount = phase140EntityName(row) === 'HEMA' && hemaChildYearDayCount > sharedYearDayCount
            ? hemaChildYearDayCount
            : sharedYearDayCount;
          const yearProjected = adjustedYearDayCount && adjustedYearDayCount !== rowYearDayCount
            ? (ytdAmount / adjustedYearDayCount) * 365
            : phase140Number(row?.yearProjected);
          const ytdTarget = yearTarget > 0 && adjustedYearDayCount > 0
            ? (yearTarget / 365) * adjustedYearDayCount
            : null;
          return {
            ...row,
            achieved: monthlyTarget > 0 ? monthlyAmount / monthlyTarget : row?.achieved,
            status: monthlyTarget > 0 ? (monthlyAmount >= monthlyTarget ? 'Pass' : 'Fail') : row?.status,
            projectedAchieved: monthlyTarget > 0 ? monthlyProjected / monthlyTarget : null,
            projectedStatus: monthlyTarget > 0 ? (monthlyProjected >= monthlyTarget ? 'Pass' : 'Fail') : '-',
            ytdTarget,
            ytdTargetAchieved: ytdTarget > 0 ? ytdAmount / ytdTarget : null,
            ytdTargetStatus: ytdTarget > 0 ? (ytdAmount >= ytdTarget ? 'Pass' : 'Fail') : '-',
            yearDayCount: adjustedYearDayCount || row?.yearDayCount,
            yearProjected,
            yearYtdStatus: yearTarget > 0 ? (ytdAmount >= yearTarget ? 'Pass' : 'Fail') : '-',
            yearProjectedAchieved: yearTarget > 0 ? yearProjected / yearTarget : null,
            yearProjectedStatus: yearTarget > 0 ? (yearProjected >= yearTarget ? 'Pass' : 'Fail') : '-',
          };
        }),
      };
    }),
  };
};

const phase148PrepareReport = (report) => (
  phase148AddYearProjectedAchievement(phase140ApplyHemaTargetFallback(report))
);

// The column headings take `t`: the screen passes its own (so they follow the language picker), the PDF export
// passes the English one — see buildExportPayload. `key`, `type` and `group` are what the code and the server
// read; only `label` is words.
const salesReportT = (key, options) => i18n.t(key, options);

const weeklyColumns = (meta, t = salesReportT) => {
  const currency = meta.currency;
  const date = phase131SnapshotDateLabel(meta.monthSnapshotEndDate);
  return [
    { key: 'dimensionValue', label: '', type: 'dimension', group: 'name' },
    { key: 'qtyCurrent', label: t('xSales.col.qty'), type: 'qty', group: 'currentQty' },
    { key: 'qtyLy', label: t('xSales.col.qtyLy'), type: 'qty', group: 'currentQty' },
    { key: 'qtyGrowthLy', label: t('xSales.col.growthLy'), type: 'growth', group: 'currentQty' },
    { key: 'qtyMix', label: t('xSales.col.mix'), type: 'percent', group: 'currentQty' },
    { key: 'amountCurrent', label: t('xSales.col.amount', { currency }), type: 'money', group: 'currentAmount' },
    { key: 'amountLy', label: t('xSales.col.amountLy', { currency }), type: 'money', group: 'currentAmount' },
    { key: 'amountGrowthLy', label: t('xSales.col.growthLy'), type: 'growth', group: 'currentAmount' },
    { key: 'amountMix', label: t('xSales.col.mix'), type: 'percent', group: 'currentAmount' },
    { key: 'qtyPrev1', label: t('xSales.col.prevQty', { week: meta.prevWeek1 }), type: 'qty', group: 'prev1Qty' },
    { key: 'qtyGrowthPrev1', label: t('xSales.col.growthVs', { week: meta.week, prev: meta.prevWeek1 }), type: 'growth', group: 'prev1Qty' },
    { key: 'amountPrev1', label: t('xSales.col.prevAmount', { week: meta.prevWeek1, currency }), type: 'money', group: 'prev1Qty' },
    { key: 'amountGrowthPrev1', label: t('xSales.col.growthVs', { week: meta.week, prev: meta.prevWeek1 }), type: 'growth', group: 'prev1Qty' },
    { key: 'qtyPrev2', label: t('xSales.col.prevQty', { week: meta.prevWeek2 }), type: 'qty', group: 'prev2Qty' },
    { key: 'qtyGrowthPrev2', label: t('xSales.col.growthVs', { week: meta.week, prev: meta.prevWeek2 }), type: 'growth', group: 'prev2Qty' },
    { key: 'amountPrev2', label: t('xSales.col.prevAmount', { week: meta.prevWeek2, currency }), type: 'money', group: 'prev2Qty' },
    { key: 'amountGrowthPrev2', label: t('xSales.col.growthVs', { week: meta.week, prev: meta.prevWeek2 }), type: 'growth', group: 'prev2Qty' },
    { key: 'target', label: t('xSales.col.weeklyTarget', { currency }), type: 'money', group: 'target' },
    { key: 'achieved', label: t('xSales.col.weeklyAchieved'), type: 'percent', group: 'target' },
    { key: 'status', label: t('xSales.col.weeklyStatus'), type: 'text', group: 'target' },
    { key: 'monthSnapshotAmount', label: t('xSales.col.mtdAmountSnap', { date, currency }), type: 'money', group: 'monthSnapshot' },
    { key: 'monthSnapshotTarget', label: t('xSales.col.monthlyTargetSnap', { date, currency }), type: 'money', group: 'monthSnapshot' },
    { key: 'monthSnapshotAchieved', label: t('xSales.col.mtdAchievedSnap', { date }), type: 'percent', group: 'monthSnapshot' },
    { key: 'monthSnapshotStatus', label: t('xSales.col.mtdStatusSnap', { date }), type: 'text', group: 'monthSnapshot' },
  ];
};

const monthlyColumns = (meta, t = salesReportT) => {
  const currency = meta.currency;
  return [
    { key: 'dimensionValue', label: '', type: 'dimension', group: 'name' },
    { key: 'qtyCurrent', label: t('xSales.col.qty'), type: 'qty', group: 'monthQty' },
    { key: 'qtyLyMtd', label: t('xSales.col.qtyLyMtd'), type: 'qty', group: 'monthQty' },
    { key: 'qtyGrowthLy', label: t('xSales.col.growthLy'), type: 'growth', group: 'monthQty' },
    { key: 'qtyMix', label: t('xSales.col.mix'), type: 'percent', group: 'monthQty' },
    { key: 'amountCurrent', label: t('xSales.col.amountNl', { currency }), type: 'money', group: 'monthAmount' },
    { key: 'amountLyMtd', label: t('xSales.col.amountLyMtd', { currency }), type: 'money', group: 'monthAmount' },
    { key: 'amountGrowthLy', label: t('xSales.col.growthLy'), type: 'growth', group: 'monthAmount' },
    { key: 'amountMix', label: t('xSales.col.mix'), type: 'percent', group: 'monthAmount' },
    { key: 'ytdQtyCurrent', label: t('xSales.col.ytdQty'), type: 'qty', group: 'ytdQty' },
    { key: 'ytdQtyLy', label: t('xSales.col.ytdQtyLyMtd'), type: 'qty', group: 'ytdQty' },
    { key: 'ytdQtyGrowth', label: t('xSales.col.ytdGrowth'), type: 'growth', group: 'ytdQty' },
    { key: 'ytdQtyMix', label: t('xSales.col.ytdMix'), type: 'percent', group: 'ytdQty' },
    { key: 'ytdAmountCurrent', label: t('xSales.col.ytdAmount', { currency }), type: 'money', group: 'ytdAmount' },
    { key: 'ytdAmountLy', label: t('xSales.col.ytdAmountLyMtd', { currency }), type: 'money', group: 'ytdAmount' },
    { key: 'ytdAmountGrowth', label: t('xSales.col.ytdAmountGrowth'), type: 'growth', group: 'ytdAmount' },
    { key: 'ytdAmountMix', label: t('xSales.col.ytdAmountMix'), type: 'percent', group: 'ytdAmount' },
    { key: 'target', label: t('xSales.col.monthlyTarget', { currency }), type: 'money', group: 'target' },
    { key: 'achieved', label: t('xSales.col.mtdAchieved'), type: 'percent', group: 'target' },
    { key: 'status', label: t('xSales.col.mtdStatus'), type: 'text', group: 'target' },
    { key: 'ytdTarget', label: t('xSales.col.ytdTarget', { currency }), type: 'money', group: 'ytdTarget' },
    { key: 'ytdTargetAchieved', label: t('xSales.col.ytdTargetPct'), type: 'percent', group: 'ytdTarget' },
    { key: 'ytdTargetStatus', label: t('xSales.col.ytdStatus'), type: 'text', group: 'ytdTarget' },
    { key: 'yearTarget', label: t('xSales.col.yearlyTarget', { currency }), type: 'money', group: 'yearTarget' },
    { key: 'yearAchieved', label: t('xSales.col.yearlyTargetPct'), type: 'percent', group: 'yearTarget' },
    { key: 'yearYtdStatus', label: t('xSales.col.yearlyStatus'), type: 'text', group: 'yearTarget' },
    { key: 'yearDayCount', label: t('xSales.col.yearlyDays'), type: 'qty', group: 'yearProjected' },
    { key: 'yearProjected', label: t('xSales.col.yearlyProjected', { currency }), type: 'money', group: 'yearProjected' },
    { key: 'yearProjectedAchieved', label: t('xSales.col.projectedPct'), type: 'percent', group: 'yearProjected' },
    { key: 'yearProjectedStatus', label: t('xSales.col.yearlyProjectedStatus'), type: 'text', group: 'yearProjected' },
  ];
};

const renderCell = (row, column, symbol) => {
  const value = row[column.key];
  if (column.type === 'money') return formatMoney(value, symbol);
  if (column.type === 'qty') return formatNumber(value);
  if (column.type === 'growth') return <GrowthCell value={value} />;
  if (column.key === 'achieved' || column.key === 'projectedAchieved' || column.key === 'ytdTargetAchieved' || column.key === 'yearAchieved' || column.key === 'yearProjectedAchieved' || column.key === 'monthSnapshotAchieved') return <TargetCell value={value} />;
  if (column.key === 'status' || column.key === 'projectedStatus' || column.key === 'ytdTargetStatus' || column.key === 'yearStatus' || column.key === 'yearYtdStatus' || column.key === 'yearProjectedStatus' || column.key === 'monthSnapshotStatus') return <StatusCell value={value} />;
  if (column.type === 'percent') return formatPercent(value);
  return value || '-';
};

const columnClass = (columns, column, index) => {
  const previous = columns[index - 1];
  const start = index > 0 && previous?.group !== column.group ? 'group-start' : '';
  const target = (column.group === 'target' || column.group === 'ytdTarget' || column.group === 'yearTarget') && previous?.group !== column.group ? 'target-start' : '';
  const projectedStart = column.group === 'monthProjection' && previous?.group !== column.group ? 'projection-start' : '';
  const snapshotStart = column.key === 'monthSnapshotAmount' ? 'snapshot-start' : '';
  const snapshotEnd = column.key === 'monthSnapshotStatus' ? 'snapshot-end' : '';
  return `col-${column.type} col-${column.key} ${start} ${target} ${projectedStart} ${snapshotStart} ${snapshotEnd}`;
};

const renderHeaderLabel = (label) => String(label || '').split('\n').map((part, index) => (
  <React.Fragment key={`${part}-${index}`}>
    {index > 0 && <br />}
    {part}
  </React.Fragment>
));




const salesReportHighlightSummaryRow = (row) => {
  const value = String(row?.dimensionValue || '').trim();
  return /^Top\s*\d+$/i.test(value) || /^Rank\s*>\s*\d+$/i.test(value);
};


const salesReportOrderEntity3Rows = (section) => {
  const rows = Array.isArray(section?.rows) ? section.rows : [];
  const key = String(section?.key || '').trim().toLowerCase();
  const label = String(section?.label || '').trim().toLowerCase();
  if (key !== 'entity' && key !== 'entity3' && label !== 'entity 3') return rows;

  const used = new Set();
  const take = (name) => {
    const index = rows.findIndex((row, rowIndex) => {
      if (used.has(rowIndex)) return false;
      return String(row?.dimensionValue || '').trim().toUpperCase() === name;
    });
    if (index < 0) return [];
    used.add(index);
    return [rows[index]];
  };

  const ordered = [];
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    if (used.has(rowIndex)) continue;
    const row = rows[rowIndex];
    const value = String(row?.dimensionValue || '').trim().toUpperCase();

    if (value === 'HEMA') {
      used.add(rowIndex);
      ordered.push(row, ...take('HEMA_EAST'), ...take('HEMA_WEST'));
      continue;
    }

    if (value === 'HEMA_EAST' || value === 'HEMA_WEST') {
      const parent = take('HEMA');
      if (parent.length) {
        ordered.push(...parent, ...take('HEMA_EAST'), ...take('HEMA_WEST'));
      } else {
        used.add(rowIndex);
        ordered.push(row);
      }
      continue;
    }

    used.add(rowIndex);
    ordered.push(row);
  }

  return ordered;
};


const salesReportHemaRowClass = (row) => {
  const value = String(row?.dimensionValue || '').trim().toUpperCase();
  if (value === 'HEMA') return 'hema-parent-row';
  if (value === 'HEMA_EAST' || value === 'HEMA_WEST') return 'hema-child-row';
  return '';
};

const reportLabel = (type, t) => {
  if (type === 'weekly2') return t('xSales.reportWeekly2');
  if (type === 'weeklyFlash') return t('xSales.reportWeeklyFlash');
  if (type === 'monthly') return t('xSales.reportMonthly');
  return t('xSales.reportWeekly');
};

const flashNorm = (value) => String(value || '').trim().toLowerCase();

const flashSection = (report, keys = []) => {
  const normalizedKeys = keys.map(flashNorm);
  return (report?.sections || []).find((section) => (
    normalizedKeys.includes(flashNorm(section?.key))
    || normalizedKeys.includes(flashNorm(section?.label))
  ));
};

const flashRows = (section) => (Array.isArray(section?.rows) ? section.rows : []);

const flashFindRow = (section, names = []) => {
  const normalizedNames = names.map(flashNorm);
  return flashRows(section).find((row) => normalizedNames.includes(flashNorm(row?.dimensionValue)));
};

const flashEntityRow = (report, names = []) => (
  flashFindRow(flashSection(report, ['entity', 'entity3', 'entity 3']), names)
  || flashTotalRow(flashSection(report, ['entity', 'entity3', 'entity 3']))
);

const flashTotalRow = (section) => (
  flashRows(section).find((row) => flashNorm(row?.dimensionValue) === 'total' || flashNorm(row?.rowType) === 'total')
);

const flashNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const flashShare = (value, total) => {
  const denominator = flashNumber(total);
  if (!denominator) return null;
  return flashNumber(value) / denominator;
};

const flashMetric = (row, key) => (row ? row[key] : null);

const flashBlockConfigs = [
  {
    key: 'hema',
    title: 'HEMA Revenue',
    source: 'entity',
    names: ['HEMA', 'TOTAL'],
    fallbackTotal: true,
  },
  {
    key: 'wholesale',
    title: 'Wholesale',
    source: 'channel',
    names: ['B2B', 'Wholesale'],
  },
  {
    key: 'dtc',
    title: 'DTC',
    source: 'channel',
    names: ['DTC/HCC', 'DTC', 'Ecommerce', 'Online / B2C'],
  },
  {
    key: 'wholesale-east',
    title: 'Wholesale East',
    breakoutKey: 'wholesaleEast',
    names: ['HEMA_EAST', 'Wholesale East'],
  },
  {
    key: 'wholesale-west',
    title: 'Wholesale West',
    breakoutKey: 'wholesaleWest',
    names: ['HEMA_WEST', 'Wholesale West'],
  },
  {
    key: 'dtc-shopify',
    title: 'DTC Shopify',
    source: 'channel2',
    names: ['Shopify', 'DTC Shopify', 'Ecommerce', 'Online / B2C'],
  },
  {
    key: 'dtc-amazon',
    title: 'DTC Amazon',
    source: 'channel2',
    names: ['Amazon', 'DTC Amazon'],
  },
];

// A block's `title` stays the English name (it keys the rows); this is what the block prints.
const flashBlockTitle = (block, t) => {
  if (block.key === 'hema') return t('xSales.flashHemaRevenue');
  if (block.key === 'wholesale') return t('xSales.flashWholesale');
  if (block.key === 'wholesale-east') return t('xSales.flashWholesaleEast');
  if (block.key === 'wholesale-west') return t('xSales.flashWholesaleWest');
  return block.title;
};

const weeklyFlashWholesaleBreakouts = [
  { key: 'wholesaleEast', entity: 'HEMA_EAST', names: ['HEMA_EAST'] },
  { key: 'wholesaleWest', entity: 'HEMA_WEST', names: ['HEMA_WEST'] },
];

const buildWeeklyFlashBreakoutFilters = (baseFilters, entities, reportType, week) => ({
  ...baseFilters,
  reportType,
  entities,
  channels: [],
  channel2s: ['Wholesale'],
  week: reportType === 'weekly' ? week : 'All',
  month: reportType === 'weekly' ? 'All' : baseFilters.month,
  quarter: 'All',
});

const flashBlockLayout = [
  ['hema', 'wholesale', 'dtc'],
  ['wholesale-east', 'wholesale-west', 'dtc-shopify', 'dtc-amazon'],
];

const WeeklyFlashValue = ({ value, type = 'money', symbol = '' }) => {
  let rendered = '-';
  if (type === 'money') rendered = formatMoney(value, symbol);
  if (type === 'qty') rendered = formatNumber(value);
  if (type === 'percent') rendered = formatPercent(value);
  if (type === 'growth') rendered = <GrowthCell value={value} />;

  return (
    <span className="srd-flash-value">{rendered}</span>
  );
};

const WeeklyFlashBlock = ({ title, row, weeklyRow, hemaTotal, weeklyHemaTotal, weeklyLabel, symbol }) => {
  const { t } = useTranslation();
  const rows = [
    {
      label: weeklyLabel || t('xSales.flashWeekly'),
      sourceRow: weeklyRow,
      sourceTotal: weeklyHemaTotal,
      revenueKey: 'amountCurrent',
      revenueGrowthKey: 'amountGrowthLy',
      qtyKey: 'qtyCurrent',
      qtyGrowthKey: 'qtyGrowthLy',
    },
    {
      label: 'MTD',
      sourceRow: row,
      sourceTotal: hemaTotal,
      revenueKey: 'amountCurrent',
      revenueGrowthKey: 'amountGrowthLy',
      qtyKey: 'qtyCurrent',
      qtyGrowthKey: 'qtyGrowthLy',
    },
    {
      label: 'YTD',
      sourceRow: row,
      sourceTotal: hemaTotal,
      revenueKey: 'ytdAmountCurrent',
      revenueGrowthKey: 'ytdAmountGrowth',
      qtyKey: 'ytdQtyCurrent',
      qtyGrowthKey: 'ytdQtyGrowth',
    },
  ];

  return (
    <section className="srd-flash-block">
      <div className="srd-flash-table-wrap">
        <table className="srd-flash-table">
          <thead>
            <tr>
              <th>{title}</th>
              <th>{t('xSales.flashColRevenue')}</th>
              <th>{t('xSales.flashColGrowth')}</th>
              <th>{t('xSales.flashColMixHema')}</th>
              <th>{t('xSales.flashColQty')}</th>
              <th>{t('xSales.flashColGrowth')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((metricRow) => {
              const currentRow = metricRow.sourceRow;
              const currentTotal = metricRow.sourceTotal;
              const revenue = flashMetric(currentRow, metricRow.revenueKey);
              return (
                <tr key={`${title}-${metricRow.label}`}>
                  <th>{metricRow.label}</th>
                  <td><WeeklyFlashValue value={revenue} symbol={symbol} /></td>
                  <td><WeeklyFlashValue value={flashMetric(currentRow, metricRow.revenueGrowthKey)} type="growth" /></td>
                  <td><WeeklyFlashValue value={flashShare(revenue, flashMetric(currentTotal, metricRow.revenueKey))} type="percent" /></td>
                  <td><WeeklyFlashValue value={flashMetric(currentRow, metricRow.qtyKey)} type="qty" /></td>
                  <td><WeeklyFlashValue value={flashMetric(currentRow, metricRow.qtyGrowthKey)} type="growth" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!row && <div className="srd-flash-missing">{t('xSales.flashNoRow')}</div>}
    </section>
  );
};

const WeeklyFlashReportView = React.memo(({ report, meta, filters }) => {
  const { t } = useTranslation();
  const symbol = meta?.currencySymbol || '';
  const entitySection = flashSection(report, ['entity', 'entity3', 'entity 3']);
  const channelSection = flashSection(report, ['channel']);
  const channel2Section = flashSection(report, ['channel2', 'channel 2', 'subchannel']);
  const sectionsBySource = {
    entity: entitySection,
    channel: channelSection,
    channel2: channel2Section,
  };
  const weeklyReport = report?.weeklyFlashWeeklyReport;
  const weeklySectionsBySource = {
    entity: flashSection(weeklyReport, ['entity', 'entity3', 'entity 3']),
    channel: flashSection(weeklyReport, ['channel']),
    channel2: flashSection(weeklyReport, ['channel2', 'channel 2', 'subchannel']),
  };
  const entityTotal = flashTotalRow(entitySection);
  const hemaTotal = flashFindRow(entitySection, ['HEMA']) || entityTotal;
  const weeklyEntityTotal = flashTotalRow(weeklySectionsBySource.entity);
  const weeklyHemaTotal = flashFindRow(weeklySectionsBySource.entity, ['HEMA']) || weeklyEntityTotal;
  const weeklyLabel = meta?.weeklyFlashWeek || filters.week || t('xSales.flashWeekly');
  const blocks = flashBlockConfigs.map((config) => ({
    ...config,
    row: config.breakoutKey
      ? report?.weeklyFlashBreakouts?.[config.breakoutKey]?.row
      : flashFindRow(sectionsBySource[config.source], config.names)
      || (config.fallbackTotal ? entityTotal : null),
    weeklyRow: config.breakoutKey
      ? report?.weeklyFlashBreakouts?.[config.breakoutKey]?.weeklyRow
      : flashFindRow(weeklySectionsBySource[config.source], config.names)
      || (config.fallbackTotal ? weeklyEntityTotal : null),
  }));
  const blockByKey = new Map(blocks.map((block) => [block.key, block]));
  const headerRow = hemaTotal || entityTotal;

  return (
    <section className="srd-flash-report">
      <div className="srd-flash-hero">
        <div>
          <p>{t('xSales.flashEyebrow')}</p>
          <h2>{t('xSales.reportWeeklyFlash')}</h2>
          <span>
            {t('xSales.flashPeriod', {
              week: weeklyLabel,
              month: meta?.month || filters.month,
              year: meta?.year || filters.year,
              currency: meta?.currency || filters.currency,
            })}
          </span>
        </div>
        <div className="srd-flash-kpis">
          <div>
            <small>{t('xSales.kpiRevenue', { period: weeklyLabel })}</small>
            <strong>{formatMoney(weeklyHemaTotal?.amountCurrent, symbol)}</strong>
          </div>
          <div>
            <small>{t('xSales.kpiGrowth', { period: weeklyLabel })}</small>
            <strong><GrowthCell value={weeklyHemaTotal?.amountGrowthLy} /></strong>
          </div>
          <div>
            <small>{t('xSales.kpiRevenue', { period: 'MTD' })}</small>
            <strong>{formatMoney(headerRow?.amountCurrent, symbol)}</strong>
          </div>
          <div>
            <small>{t('xSales.kpiGrowth', { period: 'MTD' })}</small>
            <strong><GrowthCell value={headerRow?.amountGrowthLy} /></strong>
          </div>
          <div>
            <small>{t('xSales.kpiRevenue', { period: 'YTD' })}</small>
            <strong>{formatMoney(headerRow?.ytdAmountCurrent, symbol)}</strong>
          </div>
          <div>
            <small>{t('xSales.kpiGrowth', { period: 'YTD' })}</small>
            <strong><GrowthCell value={headerRow?.ytdAmountGrowth} /></strong>
          </div>
        </div>
      </div>

      <div className="srd-flash-note">
        {t('xSales.flashNote')}
      </div>

      <div className="srd-flash-blocks">
        {flashBlockLayout.map((column, columnIndex) => (
          <div className="srd-flash-column" key={`flash-column-${columnIndex}`}>
            {column.map((blockKey) => {
              const block = blockByKey.get(blockKey);
              if (!block) return null;
              return (
                <WeeklyFlashBlock
                  key={block.key}
                  title={flashBlockTitle(block, t)}
                  row={block.row}
                  weeklyRow={block.weeklyRow}
                  hemaTotal={hemaTotal}
                  weeklyHemaTotal={weeklyHemaTotal}
                  weeklyLabel={weeklyLabel}
                  symbol={symbol}
                />
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
});

const SalesReportLoading = React.memo(() => {
  const { t } = useTranslation();
  return (
    <div className="srd-report-loading" role="status" aria-live="polite">
      <span className="srd-report-loading-spinner" aria-hidden="true" />
      <strong>{t('xSales.loading')}</strong>
    </div>
  );
});

const SalesReportSection = React.memo(({ section, columns, currencySymbol }) => {
  const { t } = useTranslation();
  const sectionColumns = useMemo(() => phase132ColumnsForSection(columns, section), [columns, section]);
  const rows = useMemo(() => salesReportOrderEntity3Rows(section), [section]);

  return (
    <section className={`srd-section${section.hideTarget ? ' srd-hide-target' : ''}`} data-section-key={section.key}>
      <div className="srd-section-header">
        <h3>{section.label}</h3>
        <strong>{section.topN ? t('xSales.topN', { n: section.topN }) : t('xSales.all')}</strong>
      </div>
      <div className="srd-table-wrap">
        <table>
          <thead>
            <tr>
              {sectionColumns.map((column, index) => (
                <th key={column.key} className={columnClass(sectionColumns, column, index)}>
                  {renderHeaderLabel(index === 0 ? section.label : column.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`${section.key}-${row.dimensionValue}-${row.rowType}-${rowIndex}`} className={[salesReportHemaRowClass(row), row.rowType, salesReportHighlightSummaryRow(row) ? 'summary-highlight' : ''].filter(Boolean).join(' ')}>
                {sectionColumns.map((column, index) => (
                  <td key={column.key} className={columnClass(sectionColumns, column, index)}>
                    {renderCell(row, column, currencySymbol)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
});

const Weekly2Cell = ({ value, symbol, percent = false }) => {
  const number = weekly2Number(value);
  if (percent) {
    return (
      <td className={number < 0 ? 'negative' : number > 0 ? 'good' : ''}>
        {Number.isFinite(Number(value)) ? formatPercent(number) : '-'}
      </td>
    );
  }
  return (
    <td className={number < 0 ? 'negative' : ''}>
      {number ? formatMoney(number, symbol) : '-'}
    </td>
  );
};

const Weekly2SummaryTable = ({ total, meta, symbol, selectedQuarter = 'All', collapsedQuarters = {} }) => {
  const { t } = useTranslation();
  const groups = weekly2QuarterGroups(meta, selectedQuarter);
  if (!total || !groups.length) return null;

  const columns = [
    ...groups.flatMap((group) => {
    const weeks = group.weeks || [];
    const spacerColumns = ['total', 'attn', 'py', 'pq'].map((part) => ({
      key: `${group.key}-spacer-${part}`,
      type: 'spacer',
    }));
    if (collapsedQuarters[group.key]) {
      return spacerColumns;
    }
    return [
      ...weeks.map((week, index) => ({
        key: `${group.key}-${week}-${index}`,
        label: weekly2WeekKey(week),
        month: weekly2MonthForWeek(group, index),
        group,
        weeks: weeks.slice(0, index + 1),
        type: 'week',
      })),
      ...spacerColumns,
    ];
    }),
    ...['year-total', 'year-attn', 'year-py'].map((part) => ({
      key: `year-spacer-${part}`,
      type: 'spacer',
    })),
    ...groups.map((group) => ({
      key: `${group.key}-target-spacer`,
      type: 'spacer',
    })),
    {
      key: 'total-target-spacer',
      type: 'spacer',
    },
  ];

  const valueFor = (column, rowKey) => {
    if (column.type === 'spacer') return null;
    const actual = (column.weeks || []).reduce((sum, week) => sum + weekly2Number(weekly2WeekValue(total, column.group, week)), 0);
    const groupTarget = weekly2Number(total.targetByQuarter?.[column.group.key]);
    const groupLy = weekly2Number(total.targetByQuarterLy?.[column.group.key]) || weekly2QuarterTotal(total, column.group, 'amountLyByWeek');
    const quarterDays = weekly2QuarterDayCount(column.group, meta?.year);
    const elapsedDays = weekly2ElapsedQuarterDays(column.group, meta?.year, column.weeks?.length || 1);
    const ly = quarterDays ? (groupLy / quarterDays) * elapsedDays : 0;
    const target = quarterDays ? (groupTarget / quarterDays) * elapsedDays : 0;
    if (rowKey === 'attain') return target ? actual / target : null;
    if (rowKey === 'ly') return ly;
    if (rowKey === 'target') return target;
    return actual;
  };

  return (
    <section className="srd-weekly2-summary">
      <div className="srd-weekly2-summary-wrap">
        <table className="srd-weekly2-summary-table">
          <colgroup>
            <col className="currency-col" />
            <col className="label-col" />
            {columns.map((column) => <col key={column.key} className="week-col" />)}
          </colgroup>
          <tbody>
            <tr>
              <th className="currency" rowSpan="4">{meta?.currency || ''}</th>
              <th className="label">{t('xSales.w2AccumAttain')}</th>
              {columns.map((column) => (
                <td key={`attain-${column.key}`} className={column.type === 'spacer' ? 'spacer' : 'percent'}>
                  {column.type === 'spacer' ? '' : formatPercent(valueFor(column, 'attain'))}
                </td>
              ))}
            </tr>
            <tr>
              <th className="label">LY</th>
              {columns.map((column) => (
                <td key={`ly-${column.key}`} className={column.type === 'spacer' ? 'spacer' : ''}>
                  {column.type === 'spacer' ? '' : formatMoney(valueFor(column, 'ly'), symbol)}
                </td>
              ))}
            </tr>
            <tr>
              <th className="label">{t('xSales.target')}</th>
              {columns.map((column) => (
                <td key={`target-${column.key}`} className={column.type === 'spacer' ? 'spacer' : ''}>
                  {column.type === 'spacer' ? '' : formatMoney(valueFor(column, 'target'), symbol)}
                </td>
              ))}
            </tr>
            <tr>
              <th className="label">{t('xSales.actual')}</th>
              {columns.map((column) => (
                <td key={`actual-${column.key}`} className={column.type === 'spacer' ? 'spacer' : ''}>
                  {column.type === 'spacer' ? '' : formatMoney(valueFor(column, 'actual'), symbol)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
};

const Weekly2MiniTable = ({
  title,
  section,
  meta,
  symbol,
  limit,
  selectedQuarter = 'All',
  collapsedQuarters = {},
  onToggleQuarter,
  defaultCollapsed = false,
}) => {
  const { t } = useTranslation();
  const sourceRows = useMemo(() => weekly2DisplayRows(section, limit), [section, limit]);
  const [expanded, setExpanded] = useState({});
  const groups = weekly2QuarterGroups(meta, selectedQuarter);
  const year = meta?.year || '';
  useEffect(() => {
    if (!defaultCollapsed) return;
    const nextExpanded = {};
    sourceRows.forEach((row) => {
      if (row.rowType === 'parent' || row.hasChildren) {
        const key = row.nodeKey || String(row.dimensionValue || '').trim().toUpperCase();
        nextExpanded[key] = false;
      }
    });
    setExpanded(nextExpanded);
  }, [defaultCollapsed, sourceRows]);
  const rows = sourceRows.filter((row) => {
    if (row.rowType === 'child') {
      const parentKey = String(row.parentValue || '').trim().toUpperCase();
      return expanded[parentKey] !== false;
    }
    if (row.rowType === 'type') {
      return expanded[row.parentKey] !== false;
    }
    if (row.rowType === 'model') {
      return expanded[row.categoryParentKey] !== false && expanded[row.parentKey] !== false;
    }
    return true;
  });
  if (!rows.length) return null;
  // The two total rows are named by weekly2DisplayRows in English and that name keys them; only the print moves.
  const rowName = (value) => {
    if (value === 'By Product Line') return t('xSales.byProductLine');
    if (value === 'By Category') return t('xSales.byCategory');
    return value;
  };

  return (
    <section className="srd-weekly2-block">
      <div className="srd-weekly2-block-title">
        <h3>{title}</h3>
        <span>{t('xSales.rows', { n: rows.length })}</span>
      </div>
      <div className="srd-weekly2-table-wrap">
        <table className="srd-weekly2-table">
          <colgroup>
            <col className="dimension-col" />
            {groups.flatMap((group) => [
              ...(!collapsedQuarters[group.key] ? (group.weeks || []).map((week, index) => (
                <col key={`${group.key}-week-col-${week}-${index}`} className="week-col" />
              )) : []),
              <col key={`${group.key}-total-col`} className="metric-col" />,
              <col key={`${group.key}-attn-col`} className="metric-col" />,
              <col key={`${group.key}-py-col`} className="metric-col" />,
              <col key={`${group.key}-pq-col`} className="metric-col" />,
            ])}
            <col className="metric-col" />
            <col className="metric-col" />
            <col className="metric-col" />
            {groups.map((group) => <col key={`${group.key}-target-col`} className="metric-col" />)}
            <col className="metric-col" />
          </colgroup>
          <thead>
            <tr>
              <th className="dimension" rowSpan="3">{t('xSales.w2WeeklyReport')}</th>
              {groups.map((group) => {
                const isCollapsed = Boolean(collapsedQuarters[group.key]);
                return (
                  <th key={group.key} className="quarter" colSpan={(isCollapsed ? 0 : group.weeks?.length || 0) + 4}>
                    <button
                      type="button"
                      className="srd-weekly2-quarter-toggle"
                      onClick={() => onToggleQuarter?.(group.key)}
                      aria-label={isCollapsed ? t('xSales.expandName', { name: group.label }) : t('xSales.collapseName', { name: group.label })}
                    >
                      {isCollapsed ? '+' : '−'}
                    </button>
                    {group.label}
                  </th>
                );
              })}
              <th className="year-total" colSpan="3">{t('xSales.yearTotal', { year })}</th>
              <th className="target" colSpan={groups.length + 1}>{t('xSales.target')}</th>
            </tr>
            <tr>
              {groups.flatMap((group) => [
                ...(!collapsedQuarters[group.key] ? (group.weeks || []).map((week, index) => (
                  <th key={`${group.key}-month-${week}-${index}`} className="month">{weekly2MonthForWeek(group, index)}</th>
                )) : []),
                <th key={`${group.key}-total`} className="total" rowSpan="2">{group.label}</th>,
                <th key={`${group.key}-attn`} className="target" rowSpan="2">{t('xSales.targetAttn')}</th>,
                <th key={`${group.key}-py`} className="variance" rowSpan="2">{t('xSales.vsPy')}</th>,
                <th key={`${group.key}-pq`} className="variance" rowSpan="2">{t('xSales.vsPq')}</th>,
              ])}
              <th className="year-total" rowSpan="2">{t('xSales.yearTotal', { year })}</th>
              <th className="target" rowSpan="2">{t('xSales.targetAttn')}</th>
              <th className="variance" rowSpan="2">{t('xSales.vsPy')}</th>
              {groups.map((group) => <th key={`${group.key}-target`} className="target" rowSpan="2">{group.targetLabel || t('xSales.quarterTarget', { year, quarter: group.key.toUpperCase() })}</th>)}
              <th className="target" rowSpan="2">{t('xSales.totalTarget')}</th>
            </tr>
            <tr>
              {groups.flatMap((group) => (!collapsedQuarters[group.key] ? (group.weeks || []).map((week, index) => (
                <th key={`${group.key}-week-${week}-${index}`}>{weekly2WeekKey(week)}</th>
              )) : []))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const yearActual = weekly2YearTotal(row);
              const yearLy = weekly2YearTotal(row, 'amountLyByWeek');
              const yearTarget = weekly2Number(row.yearTarget);
              const toggleKey = row.nodeKey || String(row.dimensionValue || '').trim().toUpperCase();
              const isParent = row.rowType === 'parent' || row.hasChildren;
              const isExpanded = expanded[toggleKey] !== false;
              const indentLevel = Number(row.level || (row.rowType === 'child' ? 1 : 0));
              return (
                <tr key={`${title}-${row.nodeKey || row.parentValue || ''}-${row.rowType || ''}-${row.dimensionValue}`} className={row.rowType || ''}>
                  <th className={`dimension ${salesReportHemaRowClass(row)}`}>
                    {isParent && (
                      <button
                        type="button"
                        className="srd-weekly2-toggle"
                        onClick={() => setExpanded((current) => ({ ...current, [toggleKey]: current[toggleKey] === false }))}
                        aria-label={isExpanded ? t('xSales.collapseName', { name: rowName(row.dimensionValue) }) : t('xSales.expandName', { name: rowName(row.dimensionValue) })}
                      >
                        {isExpanded ? '−' : '+'}
                      </button>
                    )}
                    {indentLevel > 0 && <span className="srd-weekly2-indent" style={{ width: `${indentLevel * 18}px` }} />}
                    {rowName(row.dimensionValue)}
                  </th>
                  {groups.flatMap((group) => {
                    const actual = weekly2QuarterTotal(row, group);
                    const ly = weekly2QuarterTotal(row, group, 'amountLyByWeek');
                    const target = weekly2Number(row.targetByQuarter?.[group.key]);
                    const previousQuarterActual = weekly2PreviousQuarterTotal(row, group, meta);
                    const vsPq = previousQuarterActual ? weekly2Ratio(actual, previousQuarterActual) : null;
                    return [
                      ...(!collapsedQuarters[group.key] ? (group.weeks || []).map((week, index) => (
                        <Weekly2Cell key={`${group.key}-${week}-${index}`} value={weekly2WeekValue(row, group, week)} symbol={symbol} />
                      )) : []),
                      <Weekly2Cell key={`${group.key}-total`} value={actual} symbol={symbol} />,
                      <Weekly2Cell key={`${group.key}-attn`} value={target ? actual / target : null} percent />,
                      <Weekly2Cell key={`${group.key}-py`} value={weekly2Ratio(actual, ly)} percent />,
                      <Weekly2Cell key={`${group.key}-pq`} value={vsPq} percent />,
                    ];
                  })}
                  <Weekly2Cell value={yearActual} symbol={symbol} />
                  <Weekly2Cell value={yearTarget ? yearActual / yearTarget : null} percent />
                  <Weekly2Cell value={weekly2Ratio(yearActual, yearLy)} percent />
                  {groups.map((group) => <Weekly2Cell key={`${group.key}-target`} value={row.targetByQuarter?.[group.key]} symbol={symbol} />)}
                  <Weekly2Cell value={yearTarget} symbol={symbol} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const WeeklyReport2View = React.memo(({ report, meta, filters }) => {
  const { t } = useTranslation();
  const symbol = meta?.currencySymbol || '';
  const weeklyGlobal = weekly2Section(report, 'weeklyglobal');
  const categoryTree = weekly2Section(report, 'productcategorytree');
  const line = weekly2Section(report, 'line');
  const total = p145Total(weeklyGlobal) || p145Total(line);
  const [collapsedQuarters, setCollapsedQuarters] = useState({});
  const toggleQuarter = useCallback((quarterKey) => {
    setCollapsedQuarters((current) => ({ ...current, [quarterKey]: !current[quarterKey] }));
  }, []);
  const yearActual = weekly2YearTotal(total);
  const yearTarget = weekly2Number(total?.yearTarget);
  const selectedEntity = filters.entities?.length ? selectedText(filters.entities, t('xSales.fEntity')) : t('xSales.global');

  return (
    <section className="srd-weekly2">
      <div className="srd-weekly2-hero">
        <div>
          <p>{meta?.currency || filters.currency}</p>
          <h2>{t('xSales.w2Title', { entity: selectedEntity })}</h2>
          <span>
            {filters.quarter === 'All'
              ? t('xSales.w2SubFull', { year: filters.year })
              : t('xSales.w2SubQuarter', { year: filters.year, quarter: String(filters.quarter || '').toUpperCase() })}
          </span>
        </div>
        <div className="srd-weekly2-kpis">
          <div>
            <small>{t('xSales.yearActual')}</small>
            <strong>{formatMoney(yearActual, symbol)}</strong>
          </div>
          <div>
            <small>{t('xSales.yearTarget')}</small>
            <strong>{formatMoney(yearTarget, symbol)}</strong>
          </div>
          <div>
            <small>{t('xSales.targetAttn')}</small>
            <strong>{yearTarget ? formatPercent(yearActual / yearTarget) : '-'}</strong>
          </div>
        </div>
      </div>

      <Weekly2SummaryTable
        total={total}
        meta={meta}
        symbol={symbol}
        selectedQuarter={filters.quarter}
        collapsedQuarters={collapsedQuarters}
      />
      <Weekly2MiniTable
        title={t('xSales.w2WeeklyGlobal')}
        section={weeklyGlobal}
        meta={meta}
        symbol={symbol}
        limit={80}
        selectedQuarter={filters.quarter}
        collapsedQuarters={collapsedQuarters}
        onToggleQuarter={toggleQuarter}
        defaultCollapsed
      />
      <Weekly2MiniTable
        title={t('xSales.byCategory')}
        section={categoryTree}
        meta={meta}
        symbol={symbol}
        limit={120}
        selectedQuarter={filters.quarter}
        collapsedQuarters={collapsedQuarters}
        onToggleQuarter={toggleQuarter}
      />
      <Weekly2MiniTable
        title={t('xSales.byProductLine')}
        section={line}
        meta={meta}
        symbol={symbol}
        limit={40}
        selectedQuarter={filters.quarter}
        collapsedQuarters={collapsedQuarters}
        onToggleQuarter={toggleQuarter}
      />
    </section>
  );
});


// Phase145 report-specific visual analytics - BEGIN
const p145Norm = (value) => String(value ?? '').trim().toLowerCase();
const p145Num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const p145Symbol = (currency) => ({ KRW: '₩', USD: '$', EUR: '€', SGD: 'SGD ', JPY: '¥' }[String(currency || '').toUpperCase()] || '');
const p145Money = (value, symbol = '') => {
  const n = p145Num(value);
  if (!n) return '-';
  const abs = Math.abs(n);
  let body = abs >= 1_000_000 ? `${(abs / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 0 })}M` : abs >= 1_000 ? `${(abs / 1_000).toFixed(1)}K` : abs.toLocaleString();
  return `${n < 0 ? '- ' : ''}${symbol}${body}`;
};
const p145Percent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${(n * 100).toFixed(1)}%`;
};
const p145Name = (row) => String(row?.dimensionValue ?? row?.name ?? row?.label ?? '-');
const p145Mode = (filters, meta, report) => {
  const source = [filters?.reportType, filters?.report, meta?.reportType, report?.meta?.reportType, report?.reportType].map(p145Norm).join(' ');
  return source.includes('month') ? 'monthly' : 'weekly';
};
const p145Section = (report, names) => {
  const wanted = names.map(p145Norm);
  return (report?.sections || []).find((section) => {
    const key = p145Norm(section?.key || section?.title || section?.name);
    return wanted.some((name) => key === name || key.includes(name));
  });
};
const p145Rows = (section) => (section?.rows || section?.data || []).filter((row) => p145Norm(row?.rowType) !== 'total' && p145Name(row).toLowerCase() !== 'total');
const p145Total = (section) => (section?.rows || section?.data || []).find((row) => p145Norm(row?.rowType) === 'total' || p145Name(row).toLowerCase() === 'total') || {};
const p145Amount = (row, mode) => mode === 'monthly' ? p145Num(row?.amountCurrent ?? row?.amount ?? row?.monthAmount) : p145Num(row?.amountCurrent ?? row?.amount);
const p145Ly = (row, mode) => mode === 'monthly' ? p145Num(row?.amountLyMtd ?? row?.amountLy ?? row?.amountLY) : p145Num(row?.amountLy ?? row?.amountLY);
const p145Target = (row) => p145Num(row?.target ?? row?.monthlyTarget ?? row?.weeklyTarget);
const p145Ach = (row) => Number(row?.achieved);
const p145Variance = (actual, budget) => {
  const a = p145Num(actual);
  const b = p145Num(budget);
  return {
    amount: a - b,
    percent: b ? (a - b) / b : null,
  };
};
const p145Mix = (row, mode) => {
  const value = mode === 'monthly' ? row?.amountMix ?? row?.mix : row?.amountMix ?? row?.mix;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const p145Top = (rows, getter, limit = 6) => rows.slice().sort((a, b) => Math.abs(getter(b)) - Math.abs(getter(a))).slice(0, limit);
const p145DetailFilterActive = (filters) => (
  (filters?.customerNames || []).length > 0
  || (filters?.skus || []).length > 0
  || (filters?.lines || []).length > 0
  || (filters?.regions || []).length > 0
  || (filters?.subregions || []).length > 0
  || (filters?.countries || []).length > 0
);
const p145WeekNumber = (value) => {
  const match = String(value || '').match(/\d+/);
  return match ? Number(match[0]) : 0;
};
const p145HistoryPointFromReport = (report, fallback = {}, mode = 'weekly') => {
  const section = p145Section(report, ['entity', 'entity3']) || p145Section(report, ['channel']);
  const total = p145Total(section);
  const month = report?.meta?.month || fallback.month;
  return {
    label: mode === 'monthly'
      ? (monthNames[Number(month)] || String(month || '-'))
      : (report?.meta?.week || fallback.week || '-'),
    year: report?.meta?.year || fallback.year || '',
    value: p145Amount(total, mode),
    budget: p145Target(total),
    achieved: p145Ach(total),
  };
};

const p145SeedWeeklyHistoryFromReport = (report, fallback = {}) => {
  const section = p145Section(report, ['entity', 'entity3']) || p145Section(report, ['channel']);
  const total = p145Total(section);
  const meta = report?.meta || {};
  const rawPoints = [
    { label: meta.prevWeek2, value: p145Num(total?.amountPrev2) },
    { label: meta.prevWeek1, value: p145Num(total?.amountPrev1) },
    {
      label: meta.week || fallback.week,
      value: p145Amount(total, 'weekly'),
      budget: p145Target(total),
      achieved: p145Ach(total),
    },
  ];
  return rawPoints
    .filter((point) => point.label && (point.value || point.budget))
    .map((point) => ({ year: meta.year || fallback.year || '', ...point }));
};

const p145MergeHistoryPoints = (points = []) => {
  const byLabel = new Map();
  points.forEach((point) => {
    if (!point?.label || (!point.value && !point.budget)) return;
    const key = `${point.year || ''}-${point.label}`;
    const existing = byLabel.get(key);
    if (!existing || existing.inline) byLabel.set(key, point);
  });
  return Array.from(byLabel.values());
};

const P145Card = ({ title, eyebrow, badge, children, className = '', loading = false, loadingLabel }) => {
  const { t } = useTranslation();
  return (
    <div className={`srd-v145-card ${className}`}>
      <div className="srd-v145-card-head">
        <span>{title}</span>
        <div className="srd-v145-card-head-chips">
          {loading && (
            <em className="srd-v145-card-loading" role="status" aria-live="polite">
              <span className="srd-v145-loading-spinner" aria-hidden="true" />
              {loadingLabel || t('xSales.loadingShort')}
            </em>
          )}
          {eyebrow && <em>{eyebrow}</em>}
          {badge && <em className="srd-v145-card-badge">{badge}</em>}
        </div>
      </div>
      {children}
    </div>
  );
};

const P145Kpi = ({ label, value, state }) => (
  <div className="srd-v145-kpi">
    <small>{label}</small>
    <span className={state || ''}>{value}</span>
  </div>
);

const P145ChartLoading = ({ label }) => {
  const { t } = useTranslation();
  return (
    <div className="srd-v145-chart-loading-block" role="status" aria-live="polite">
      <span className="srd-v145-loading-spinner" aria-hidden="true" />
      <span>{label || t('xSales.loadingChart')}</span>
    </div>
  );
};

const P145ChartOverlay = ({ label }) => {
  const { t } = useTranslation();
  return (
    <div className="srd-v145-chart-loading" role="status" aria-live="polite">
      <span className="srd-v145-loading-spinner" aria-hidden="true" />
      <span>{label || t('xSales.loadingChart')}</span>
    </div>
  );
};

const P145ChartFrame = ({ loading = false, label, resetKey, children }) => {
  const frameRef = useRef(null);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let rafId = 0;
    const startedAt = Date.now();
    setRendered(false);

    const checkRendered = () => {
      if (cancelled) return;
      const node = frameRef.current;
      const hasEmptyState = Boolean(node?.querySelector('.srd-v145-empty'));
      const hasRechartsMarks = Boolean(node?.querySelector(
        '.recharts-bar-rectangle, .recharts-line-curve, .recharts-line-dot, .recharts-pie-sector, .recharts-sector, .recharts-label-list text'
      ));
      const hasMapMarks = Boolean(node?.querySelector('.c150-map-wrapper .rsm-geography, .c150-map-wrapper circle'));
      const metMinimumDisplay = Date.now() - startedAt > 650;
      if (((hasRechartsMarks || hasMapMarks || hasEmptyState) && metMinimumDisplay) || Date.now() - startedAt > 10000) {
        setRendered(true);
        return;
      }
      rafId = window.requestAnimationFrame(checkRendered);
    };

    rafId = window.requestAnimationFrame(checkRendered);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
    };
  }, [resetKey]);

  return (
    <div className="c150-chart-shell" ref={frameRef}>
      {children}
      {(loading || !rendered) && <P145ChartOverlay label={label} />}
    </div>
  );
};

const P145ActualBudget = ({ total, mode, symbol, targetVisible }) => {
  const { t } = useTranslation();
  if (!targetVisible) return <div className="srd-v145-empty">{t('xSales.abNoTarget')}</div>;
  const rawRows = mode === 'monthly'
    ? [
        { label: t('xSales.abMonthActual'), actual: p145Amount(total, mode), budget: p145Target(total) },
        { label: t('xSales.abProjected'), actual: p145Num(total?.projected), budget: p145Target(total) },
        { label: t('xSales.abYtdActual'), actual: p145Num(total?.ytdAmountCurrent), budget: p145Num(total?.yearTarget) },
      ]
    : [
        { label: t('xSales.abWeekActual'), actual: p145Amount(total, mode), budget: p145Target(total) },
        { label: t('xSales.abMtdActual'), actual: p145Num(total?.monthSnapshotAmount), budget: p145Num(total?.monthSnapshotTarget) },
        { label: t('xSales.abMtdProjected'), actual: p145Num(total?.monthSnapshotAmount), budget: p145Num(total?.monthSnapshotTarget), projected: p145Num(total?.monthSnapshotProjected) },
      ];
  const seen = new Set();
  const items = rawRows.filter((row) => {
    if (!row.actual && !row.budget) return false;
    const key = `${p145Money(row.actual, symbol)}-${p145Money(row.budget, symbol)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (!items.length) return <div className="srd-v145-empty">{t('xSales.abNoComparison')}</div>;
  return (
    <div className={`ab2${items.length <= 2 ? ' ab2--large' : ''}`}>
      {items.map((row) => {
        const projVariance = row.projected ? p145Variance(row.projected, row.budget) : null;
        const variance = row.projected ? projVariance : p145Variance(row.actual, row.budget);
        const achBase = row.projected ?? row.actual;
        const ach = row.budget ? Math.round(achBase / row.budget * 100) : null;
        const actualPct = row.budget ? Math.min(100, Math.max(1, row.actual / row.budget * 100)) : 0;
        const projPct = row.projected && row.budget ? Math.min(100, Math.max(1, row.projected / row.budget * 100)) : null;
        return (
          <div className="ab2-row" key={row.label}>
            <div className="ab2-row-head">
              <strong>{row.label}</strong>
              <span>
                {row.projected
                  ? t('xSales.abLineProj', { actual: p145Money(row.actual, symbol), projected: p145Money(row.projected, symbol), target: p145Money(row.budget, symbol) })
                  : t('xSales.abLine', { actual: p145Money(row.actual, symbol), target: p145Money(row.budget, symbol) })}
              </span>
            </div>
            <div className="ab2-bar">
              {projPct !== null && <div className="ab2-bar-projected" style={{ width: `${projPct}%` }} />}
              <div className="ab2-bar-actual" style={{ width: `${actualPct}%` }} />
            </div>
            <div className="ab2-badges">
              {ach !== null && <span className="ab2-badge-ach">{t('xSales.abAchieve', { pct: ach })}</span>}
              <span className={`ab2-badge-diff ${variance.amount >= 0 ? 'good' : 'bad'}`}>{t('xSales.abDiff', { amount: p145Money(variance.amount, symbol) })}</span>
            </div>
          </div>
        );
      })}
      <div className="ab2-legend">
        <span><span className="ab2-legend-line ab2-line-budget" />{t('xSales.target')}</span>
        <span><span className="ab2-legend-line ab2-line-actual" />{t('xSales.actual')}</span>
      </div>
    </div>
  );
};

const Phase145VisualAnalytics = React.memo(({ report, meta, filters, visualHistory = [], loadingVisualHistory = false, loadingReport = false }) => {
  const { t } = useTranslation();
  const mode = useMemo(() => p145Mode(filters, meta, report), [filters, meta, report]);
  const symbol = useMemo(() => p145Symbol(filters?.currency || meta?.currency), [filters?.currency, meta?.currency]);
  const targetVisible = useMemo(() => !p145DetailFilterActive(filters), [filters]);

  // Section extraction — only re-runs when report changes
  const {
    entitySection, channelSection, customerSection,
    skuSection, lineSection, regionSection, productSection, productTypeSection,
  } = useMemo(() => ({
    entitySection: p145Section(report, ['entity', 'entity3']),
    channelSection: p145Section(report, ['channel']),
    customerSection: p145Section(report, ['customer name', 'customer']),
    skuSection: p145Section(report, ['sku']),
    lineSection: p145Section(report, ['line']),
    regionSection: p145Section(report, ['region']),
    productSection: p145Section(report, ['productcategory']),
    productTypeSection: p145Section(report, ['producttype']),
  }), [report]);

  // Row extraction — only re-runs when sections change
  const { entityRows, channelRows, customerRows, skuRows, lineRows, regionRows, productRows, productTypeRows } = useMemo(() => ({
    entityRows: p145Rows(entitySection),
    channelRows: p145Rows(channelSection),
    customerRows: p145Rows(customerSection),
    skuRows: p145Rows(skuSection),
    lineRows: p145Rows(lineSection),
    regionRows: p145Rows(regionSection),
    productRows: p145Rows(productSection),
    productTypeRows: p145Rows(productTypeSection),
  }), [entitySection, channelSection, customerSection, skuSection, lineSection, regionSection, productSection, productTypeSection]);

  const total = useMemo(() => p145Total(entitySection) || p145Total(channelSection), [entitySection, channelSection]);

  // KPI values — re-runs when total or mode changes
  const { amount, ly, budget, mtdAch, ytdAch, variance, topEntity } = useMemo(() => {
    const _amount = p145Amount(total, mode);
    const _ly = p145Ly(total, mode);
    const _budget = p145Target(total);
    const _mtdAmount = p145Num(total?.monthSnapshotAmount);
    const _mtdTarget = p145Num(total?.monthSnapshotTarget);
    const _ytdAmount = p145Num(total?.ytdAmountCurrent);
    const _yearTarget = p145Num(total?.yearTarget);
    return {
      amount: _amount,
      ly: _ly,
      budget: _budget,
      mtdAch: _mtdAmount && _mtdTarget ? _mtdAmount / _mtdTarget : null,
      ytdAch: _ytdAmount && _yearTarget ? _ytdAmount / _yearTarget : null,
      variance: p145Variance(_amount, _budget),
      topEntity: p145Top(entityRows, (row) => p145Amount(row, mode), 1)[0],
    };
  }, [total, mode, entityRows]);

  const subtitle = useMemo(() => {
    const targetRows = entityRows.filter((row) => Number.isFinite(p145Ach(row)));
    const passCount = targetRows.filter((r) => p145Ach(r) >= 1).length;
    const onTrackSuffix = targetVisible && targetRows.length > 0
      ? ` · ${t('xSales.visOnTrack', { pass: passCount, n: targetRows.length })}`
      : '';
    return mode === 'monthly'
      ? `${t('xSales.visMonthly', { month: filters?.month || meta?.month || t('xSales.selectedMonth'), year: filters?.year || meta?.year || '' }).trim()}${onTrackSuffix}`
      : `${t('xSales.visWeekly', { week: filters?.week || meta?.week || t('xSales.selectedWeek') })}${onTrackSuffix}`;
  }, [mode, filters, meta, entityRows, targetVisible, t]);

  return (
    <section className={`srd-v145 ${mode}`} aria-label={t('xSales.visAria')}>
      <div className="srd-v145-head">
        <div>
          <h2>{t('xSales.visTitle')}</h2>
          <p>{subtitle}</p>
        </div>
        <span>{loadingVisualHistory ? t('xSales.updatingTrend') : (mode === 'monthly' ? t('xSales.monthlyView') : t('xSales.weeklyView'))}</span>
      </div>

      <div className="srd-v145-kpis">
        <P145Kpi label={mode === 'monthly' ? t('xSales.kpiMonthActual') : t('xSales.kpiWeekActual')} value={p145Money(amount, symbol)} sub={ly ? `LY ${p145Money(ly, symbol)}` : 'current period'} />
        <P145Kpi label={t('xSales.target')} value={targetVisible && budget ? p145Money(budget, symbol) : '-'} />
        <P145Kpi label={t('xSales.budgetVariance')} value={targetVisible && budget ? p145Money(variance.amount, symbol) : '-'} state={targetVisible && budget && variance.amount >= 0 ? 'good' : targetVisible && budget ? 'bad' : ''} />
        <P145Kpi label={targetVisible ? t('xSales.actualTargetRatio') : t('xSales.actualMix')} value={targetVisible ? p145Percent(p145Ach(total)) : (topEntity ? p145Percent(p145Mix(topEntity, mode)) : '-')} />
        {mode === 'monthly'
          ? <P145Kpi label={t('xSales.achieveYtd')} value={ytdAch !== null ? p145Percent(ytdAch) : '-'} />
          : <P145Kpi label={t('xSales.achieveMtd')} value={mtdAch !== null ? p145Percent(mtdAch) : '-'} />
        }
      </div>

      <Phase150Charts
        report={report} meta={meta} mode={mode} symbol={symbol} total={total}
        entityRows={entityRows} channelRows={channelRows} customerRows={customerRows}
        skuRows={skuRows} lineRows={lineRows} regionRows={regionRows}
        productRows={productRows} productTypeRows={productTypeRows} visualHistory={visualHistory} targetVisible={targetVisible}
        filters={filters} loadingVisualHistory={loadingVisualHistory} loadingReport={loadingReport}
      />
    </section>
  );
});
// Phase150 Charts tab - BEGIN
const GEO_URL = '/world-110m.json';

const C150_COLORS = ['#009FE3', '#1fcfc2', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#10b981', '#f97316', '#6366f1', '#ec4899'];
const VISUAL_WEEK_HISTORY_COUNT = 4;

const COUNTRY_COORDS = {
  'USA': [-95, 37], 'United States': [-95, 37], 'US': [-95, 37],
  'Korea': [128, 36], 'South Korea': [128, 36], 'KR': [128, 36],
  'Japan': [138, 36], 'JP': [138, 36],
  'Germany': [10, 51], 'DE': [10, 51],
  'France': [2.3, 46], 'FR': [2.3, 46],
  'UK': [-1.5, 52], 'United Kingdom': [-1.5, 52], 'GB': [-1.5, 52],
  'Australia': [134, -25], 'AU': [134, -25],
  'Canada': [-96, 56], 'CA': [-96, 56],
  'China': [104, 35], 'CN': [104, 35],
  'Taiwan': [121, 24], 'TW': [121, 24],
  'Singapore': [104, 1.4], 'SG': [104, 1.4],
  'Netherlands': [5.3, 52], 'NL': [5.3, 52],
  'Spain': [-3.7, 40], 'ES': [-3.7, 40],
  'Italy': [12, 42], 'IT': [12, 42],
  'Switzerland': [8.2, 46.8], 'CH': [8.2, 46.8],
  'Sweden': [18, 60], 'SE': [18, 60],
  'Norway': [10, 62], 'NO': [10, 62],
  'Denmark': [10, 56], 'DK': [10, 56],
  'Finland': [26, 64], 'FI': [26, 64],
  'Belgium': [4.5, 50.5], 'BE': [4.5, 50.5],
  'Austria': [14.5, 47.5], 'AT': [14.5, 47.5],
  'Poland': [20, 52], 'PL': [20, 52],
  'New Zealand': [172, -41], 'NZ': [172, -41],
  'Brazil': [-52, -14], 'BR': [-52, -14],
  'Mexico': [-102, 24], 'MX': [-102, 24],
  'Hong Kong': [114, 22.3], 'HK': [114, 22.3],
  'Thailand': [101, 15], 'TH': [101, 15],
  'Indonesia': [114, -3], 'ID': [114, -3],
  'Vietnam': [108, 14], 'VN': [108, 14],
  'Malaysia': [112, 3.5], 'MY': [112, 3.5],
  'Philippines': [122, 12], 'PH': [122, 12],
  'Russia': [105, 62], 'RU': [105, 62],
  'UAE': [54, 24], 'United Arab Emirates': [54, 24],
  'South Africa': [25, -29], 'ZA': [25, -29],
  'Portugal': [-8, 39.5], 'PT': [-8, 39.5],
  'Ireland': [-8, 53], 'IE': [-8, 53],
  'Czech Republic': [15.5, 50], 'CZ': [15.5, 50],
  'Hungary': [19, 47], 'HU': [19, 47],
  'Romania': [25, 46], 'RO': [25, 46],
  'India': [78, 22], 'IN': [78, 22],
  'Sri Lanka': [81, 8], 'LK': [81, 8],
  'Pakistan': [70, 30], 'PK': [70, 30],
  'Bangladesh': [90, 24], 'BD': [90, 24],
  'Myanmar': [96, 17], 'MM': [96, 17],
  'Cambodia': [105, 12.5], 'KH': [105, 12.5],
  'Israel': [35, 31.5], 'IL': [35, 31.5],
  'Saudi Arabia': [45, 24], 'SA': [45, 24],
  'Chile': [-71, -30], 'CL': [-71, -30],
  'Argentina': [-64, -34], 'AR': [-64, -34],
  'Colombia': [-74, 4], 'CO': [-74, 4],
  'Peru': [-75, -10], 'PE': [-75, -10],
  'Morocco': [-7, 32], 'MA': [-7, 32],
  'Kenya': [38, 0], 'KE': [38, 0],
  'Greece': [21.8, 39.1], 'GR': [21.8, 39.1],
  'Luxembourg': [6.1, 49.8], 'LU': [6.1, 49.8],
  'Ukraine': [32, 49], 'UA': [32, 49],
  'Slovakia': [19.5, 48.7], 'SK': [19.5, 48.7],
  'Slovenia': [14.9, 46.1], 'SI': [14.9, 46.1],
  'Georgia': [44.8, 41.7], 'GE': [44.8, 41.7],
  'Isle of Man': [-4.5, 54.2],
  'Guernsey': [-2.5, 49.5],
  'Czechia': [15.5, 50], 'Czech': [15.5, 50],
  'Macao': [113.5, 22.2], 'Macau': [113.5, 22.2], 'MO': [113.5, 22.2],
};

const c150Fmt = (symbol) => (v) => {
  if (!Number.isFinite(v) || v === 0) return '-';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${symbol}${(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${symbol}${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${symbol}${(v / 1e3).toFixed(0)}K`;
  return `${symbol}${v.toFixed(0)}`;
};
const C150Tooltip = ({ active, payload, label, symbol }) => {
  if (!active || !payload?.length) return null;
  const fmt = c150Fmt(symbol);
  return (
    <div className="c150-tooltip">
      <p className="c150-tt-label">{label}</p>
      {payload.map((entry, i) => {
        // Read by dataKey, not by the series name: the name is translated (Target / 목표 / Mục tiêu / 目标).
        const textColor = entry.dataKey === 'budget' ? '#6b8099' : entry.color;
        const isPercent = entry.dataKey === 'ach' || entry.dataKey === 'mix' || String(entry.name || '').includes('%');
        return (
          <p key={i} style={{ color: textColor }}>
            {entry.name}: {isPercent ? `${Number(entry.value).toFixed(1)}%` : fmt(entry.value)}
          </p>
        );
      })}
    </div>
  );
};

// 1. Weekly Trend: ComposedChart bars(actual+budget) + line(ach%)
const C150WeeklyTrend = ({ total, mode, meta, symbol, history, targetVisible, loading }) => {
  const { t } = useTranslation();
  const fmt = c150Fmt(symbol);
  const histPts = (history || []).map((pt) => ({
    name: pt.label || pt.week || '',
    actual: p145Num(pt.value),
    budget: p145Num(pt.budget) || null,
    ach: Number.isFinite(pt.achieved) ? Math.round(pt.achieved * 100) : null,
  }));
  const currentWeekName = mode === 'monthly'
    ? (monthNames[Number(meta?.month)] || t('xSales.currentLabel'))
    : (meta?.week || t('xSales.currentLabel'));
  const current = {
    name: currentWeekName,
    actual: p145Amount(total, mode),
    budget: p145Target(total) || null,
    ach: Number.isFinite(p145Ach(total)) ? Math.round(p145Ach(total) * 100) : null,
  };
  // Exclude history point that matches current week to avoid duplicate label
  const histFiltered = histPts.filter((d) => d.name !== currentWeekName);
  const allPts = [...histFiltered, current].filter((d) => d.name && (d.actual || d.budget));
  const data = allPts.slice(-8);
  if (!data.length) return <div className="srd-v145-empty">{t('xSales.noTrend')}</div>;
  const maxVal = Math.max(...data.map((d) => targetVisible ? Math.max(d.actual || 0, d.budget || 0) : (d.actual || 0)), 1);
  return (
    <P145ChartFrame loading={loading} label={t('xSales.loadingTrend')} resetKey={`trend-${mode}-${currentWeekName}-${symbol}-${data.map((d) => `${d.name}:${d.actual}:${d.budget}`).join('|')}`}>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} barGap={1} barCategoryGap="20%" margin={{ top: 10, right: targetVisible ? 50 : 10, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#edf2f8" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b8099' }} interval={0} />
          <YAxis yAxisId="left" tickFormatter={fmt} tick={{ fontSize: 11, fill: '#6b8099' }} domain={[0, maxVal * 1.15]} width={70} />
          {targetVisible && <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: '#f59e0b' }} domain={[0, 130]} width={45} />}
          <Tooltip content={<C150Tooltip symbol={symbol} />} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {targetVisible && <Bar yAxisId="left" dataKey="budget" name={t('xSales.target')} fill="#a8bcd0" radius={[3, 3, 0, 0]} maxBarSize={40} isAnimationActive={false} />}
          <Bar yAxisId="left" dataKey="actual" name={t('xSales.actual')} fill="#009FE3" radius={[3, 3, 0, 0]} maxBarSize={40} isAnimationActive={false} />
          {targetVisible && <Line yAxisId="right" type="monotone" dataKey="ach" name={t('xSales.seriesAch')} stroke="#f59e0b" strokeWidth={2} dot={{ r: 4, fill: '#f59e0b' }} activeDot={{ r: 6 }} connectNulls isAnimationActive={false} />}
          {targetVisible && <ReferenceLine yAxisId="right" y={100} stroke="#f59e0b" strokeDasharray="4 4" strokeWidth={1} />}
        </ComposedChart>
      </ResponsiveContainer>
    </P145ChartFrame>
  );
};

// 2. Entity grouped bar + ach% + mix% lines
const C150EntityChart = ({ rows, mode, symbol, targetVisible }) => {
  const { t } = useTranslation();
  const fmt = c150Fmt(symbol);
  const data = rows.map((row) => ({
    name: p145Name(row),
    actual: p145Amount(row, mode),
    budget: p145Target(row) || null,
    ach: Number.isFinite(p145Ach(row)) ? Math.round(p145Ach(row) * 100) : null,
    mix: Number.isFinite(p145Mix(row, mode)) ? Math.round(p145Mix(row, mode) * 100) : null,
  }));
  if (!data.length) return <div className="srd-v145-empty">{t('xSales.noEntityData')}</div>;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 10, right: 55, left: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#edf2f8" />
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b8099' }} />
        <YAxis yAxisId="left" tickFormatter={fmt} tick={{ fontSize: 11, fill: '#6b8099' }} width={70} />
        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: '#6b8099' }} domain={[0, 130]} width={45} />
        <Tooltip content={<C150Tooltip symbol={symbol} />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {targetVisible && <Bar yAxisId="left" dataKey="budget" name={t('xSales.target')} fill="#a8bcd0" radius={[3, 3, 0, 0]} maxBarSize={36} isAnimationActive={false} />}
        <Bar yAxisId="left" dataKey="actual" name={t('xSales.actual')} fill="#009FE3" radius={[3, 3, 0, 0]} maxBarSize={36} isAnimationActive={false} />
        {targetVisible && <Line yAxisId="right" type="monotone" dataKey="ach" name={t('xSales.seriesAch')} stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} connectNulls isAnimationActive={false} />}
        <Line yAxisId="right" type="monotone" dataKey="mix" name={t('xSales.seriesMix')} stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} strokeDasharray="5 3" connectNulls isAnimationActive={false} />
        {targetVisible && <ReferenceLine yAxisId="right" y={100} stroke="#f59e0b" strokeDasharray="4 4" strokeWidth={1} />}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// 3. Channel grouped bar + ach% + mix% lines
const C150ChannelChart = ({ rows, mode, symbol, targetVisible }) => {
  const { t } = useTranslation();
  const fmt = c150Fmt(symbol);
  const data = rows.map((row) => ({
    name: p145Name(row),
    actual: p145Amount(row, mode),
    budget: p145Target(row) || null,
    ach: Number.isFinite(p145Ach(row)) ? Math.round(p145Ach(row) * 100) : null,
    mix: Number.isFinite(p145Mix(row, mode)) ? Math.round(p145Mix(row, mode) * 100) : null,
  }));
  if (!data.length) return <div className="srd-v145-empty">{t('xSales.noChannelData')}</div>;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <ComposedChart data={data} margin={{ top: 10, right: 55, left: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#edf2f8" />
        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b8099' }} />
        <YAxis yAxisId="left" tickFormatter={fmt} tick={{ fontSize: 11, fill: '#6b8099' }} width={70} />
        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: '#6b8099' }} domain={[0, 130]} width={45} />
        <Tooltip content={<C150Tooltip symbol={symbol} />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {targetVisible && <Bar yAxisId="left" dataKey="budget" name={t('xSales.target')} fill="#a8bcd0" radius={[3, 3, 0, 0]} maxBarSize={48} isAnimationActive={false} />}
        <Bar yAxisId="left" dataKey="actual" name={t('xSales.actual')} fill="#009FE3" radius={[3, 3, 0, 0]} maxBarSize={48} isAnimationActive={false} />
        {targetVisible && <Line yAxisId="right" type="monotone" dataKey="ach" name={t('xSales.seriesAch')} stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} connectNulls isAnimationActive={false} />}
        <Line yAxisId="right" type="monotone" dataKey="mix" name={t('xSales.seriesMix')} stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} strokeDasharray="5 3" connectNulls isAnimationActive={false} />
        {targetVisible && <ReferenceLine yAxisId="right" y={100} stroke="#f59e0b" strokeDasharray="4 4" strokeWidth={1} />}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// 4 & 5. Horizontal bar chart for Top 10 customer / SKU
const C150HorizTick = ({ x, y, payload }) => {
  const text = String(payload?.value || '');
  const maxW = 144;
  const charW = 6.2;
  const perLine = Math.floor(maxW / charW);
  const words = text.split(/\s+/);
  const lines = [];
  let cur = '';
  words.forEach((w) => {
    const test = cur ? `${cur} ${w}` : w;
    if (test.length > perLine && cur) { lines.push(cur); cur = w; }
    else cur = test;
  });
  if (cur) lines.push(cur);
  const lh = 13;
  const offset = -((lines.length - 1) * lh) / 2;
  return (
    <g transform={`translate(${x - 148},${y})`}>
      {lines.map((l, i) => (
        <text key={i} x={0} y={offset + i * lh} dy={4} fontSize={11} style={{ fill: 'var(--chart-label, #3d5266)' }} textAnchor="start">{l}</text>
      ))}
    </g>
  );
};

const C150HorizBar = ({ rows, mode, symbol, limit = 10, color = '#009FE3' }) => {
  const { t } = useTranslation();
  const fmt = c150Fmt(symbol);
  const isSummary = (r) => {
    const n = p145Name(r);
    const rowType = String(r?.rowType || '').toLowerCase();
    return rowType === 'summary' || rowType === 'subtotal' || /^top\s*\d+$/i.test(n) || /^rank\s*>\s*\d+$/i.test(n);
  };
  const data = p145Top(rows.filter((r) => !isSummary(r)), (r) => p145Amount(r, mode), limit)
    .map((row) => ({ name: p145Name(row), amount: p145Amount(row, mode) }));
  if (!data.length) return <div className="srd-v145-empty">{t('xSales.noData')}</div>;
  return (
    <ResponsiveContainer width="100%" height={Math.max(240, data.length * 42 + 40)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 70, left: 14, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#edf2f8" horizontal={false} />
        <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 10, fill: '#6b8099' }} />
        <YAxis type="category" dataKey="name" width={148} tick={<C150HorizTick />} />
        <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: 'rgba(10,122,110,0.05)' }} />
        <Bar dataKey="amount" name={t('xSales.seriesAmount')} fill={color} radius={[0, 4, 4, 0]} maxBarSize={32} isAnimationActive={false}>
          <LabelList dataKey="amount" position="right" formatter={fmt} style={{ fontSize: 10, fill: '#3d5266', fontWeight: 600 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

// 6. Country bubble map
const C150BubbleMap = ({ rows, mode, symbol }) => {
  const { t } = useTranslation();
  const fmt = c150Fmt(symbol);
  const [zoom, setZoom] = useState(1);
  const withCoords = rows
    .map((row) => {
      const name = p145Name(row);
      const coords = COUNTRY_COORDS[name] || null;
      const amount = p145Amount(row, mode);
      return (coords && amount > 0) ? { name, coords, amount } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.amount - a.amount);
  const maxAmt = Math.max(...withCoords.map((d) => d.amount), 1);
  if (!withCoords.length) return (
    <div className="srd-v145-empty" style={{ padding: '40px 0', textAlign: 'center' }}>
      {t('xSales.mapNoCountry')}<br />
      <small>{t('xSales.mapHint')}</small>
    </div>
  );
  return (
    <div className="c150-map-wrapper">
      <ComposableMap projectionConfig={{ scale: 160, center: [0, 15] }} style={{ width: '100%', height: 400 }}>
        <ZoomableGroup zoom={1.3} center={[0, 10]} onMoveEnd={({ zoom: z }) => setZoom(z)}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography key={geo.rsmKey} geography={geo}
                  fill="#e8f0f8" stroke="#c8d8e8" strokeWidth={0.5}
                  style={{ default: { outline: 'none' }, hover: { fill: '#d0e4f0', outline: 'none' }, pressed: { outline: 'none' } }}
                />
              ))
            }
          </Geographies>
          {[
            { name: 'NORTH AMERICA', label: t('xSales.continentNorthAmerica'), coords: [-100, 55] },
            { name: 'SOUTH AMERICA', label: t('xSales.continentSouthAmerica'), coords: [-60, -20] },
            { name: 'EUROPE', label: t('xSales.continentEurope'), coords: [15, 57] },
            { name: 'AFRICA', label: t('xSales.continentAfrica'), coords: [22, 2] },
            { name: 'ASIA', label: t('xSales.continentAsia'), coords: [88, 48] },
            { name: 'OCEANIA', label: t('xSales.continentOceania'), coords: [133, -38] },
          ].map(({ name, label, coords }) => (
            <Marker key={name} coordinates={coords}>
              <text textAnchor="middle"
                style={{ fontSize: 7, fill: '#94afc5', fontWeight: 700, letterSpacing: '0.08em', pointerEvents: 'none', textTransform: 'uppercase' }}>
                {label}
              </text>
            </Marker>
          ))}
          {withCoords.map(({ name, coords, amount }, idx) => {
            const r = Math.max(5, Math.min(18, Math.sqrt(amount / maxAmt) * 18));
            const color = C150_COLORS[idx % C150_COLORS.length];
            return (
              <Marker key={name} coordinates={coords}>
                <circle r={r} fill={color} fillOpacity={0.82} stroke={color} strokeWidth={1} strokeOpacity={0.9} />
                {zoom >= 2 && (
                  <text y={r + 9} textAnchor="middle"
                    style={{ fontSize: 8, fill: '#1e3a5f', fontWeight: 700, pointerEvents: 'none', textShadow: '0 0 3px #fff' }}>
                    {name}
                  </text>
                )}
                <title>{name}: {fmt(amount)}</title>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>
      <div className="c150-bubble-legend">
        {withCoords.slice(0, 8).map((d, idx) => (
          <span key={d.name}>
            <span className="c150-bubble-dot" style={{ background: C150_COLORS[idx % C150_COLORS.length] }} />
            <b>{d.name}</b> {fmt(d.amount)}
          </span>
        ))}
      </div>
    </div>
  );
};

// 7 & 8. Pie charts
const C150PieChart = ({ rows, mode, symbol, title }) => {
  const { t } = useTranslation();
  const fmt = c150Fmt(symbol);
  const [activeIdx, setActiveIdx] = useState(null);
  const summaryRows = rows.filter((row) => p145Norm(row?.rowType) === 'summary');
  const individualRows = rows.filter((row) => p145Norm(row?.rowType) !== 'summary');
  // When summary rows exist, Top-N + Rank->N together = grand total (no double-count)
  const totalAmt = summaryRows.length >= 2
    ? summaryRows.reduce((s, r) => s + Math.abs(p145Amount(r, mode)), 0) || 1
    : individualRows.reduce((s, r) => s + Math.abs(p145Amount(r, mode)), 0) || 1;
  const data = individualRows.map((row) => ({
    name: p145Name(row),
    value: Math.abs(p145Amount(row, mode)),
  })).filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value)
    .map((d, i) => ({
      ...d,
      pct: (d.value / totalAmt * 100).toFixed(1),
      fill: C150_COLORS[i % C150_COLORS.length],
    }));
  if (!data.length) return <div className="srd-v145-empty">{t('xSales.noData')}</div>;
  return (
    <div className="c150-pie-wrap">
      <p className="c150-pie-title">{title}</p>
      <div className="c150-pie-row">
        <div className="c150-pie-donut">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={data} cx="50%" cy="50%" innerRadius={65} outerRadius={110}
                dataKey="value" paddingAngle={2}
                activeIndex={activeIdx}
                activeShape={(props) => {
                  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
                  return (
                    <g>
                      <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 6} startAngle={startAngle} endAngle={endAngle} fill={fill} />
                      <text x={cx} y={cy - 8} textAnchor="middle" fill={fill} fontSize={13} fontWeight={700}>{payload.pct}%</text>
                      <text x={cx} y={cy + 10} textAnchor="middle" fill="#5f7898" fontSize={10}>{payload.name}</text>
                      <text x={cx} y={cy + 24} textAnchor="middle" fill="#3d5266" fontSize={11} fontWeight={600}>{fmt(payload.value)}</text>
                    </g>
                  );
                }}
                onMouseEnter={(_, i) => setActiveIdx(i)}
                onMouseLeave={() => setActiveIdx(null)}
              >
                {data.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="c150-pie-legend-custom">
          {data.map((d) => (
            <div key={d.name} className="c150-pie-legend-row">
              <span className="c150-pie-legend-dot" style={{ background: d.fill }} />
              <span className="c150-pie-legend-label" style={{ color: d.fill }}>{d.name}</span>
              <span className="c150-pie-legend-pct">{d.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Phase150Charts wrapper
const Phase150Charts = ({ report, meta, mode, symbol, total, entityRows, channelRows, customerRows, skuRows, lineRows, regionRows, productRows, productTypeRows, visualHistory, targetVisible, filters, loadingVisualHistory, loadingReport = false }) => {
  const { t } = useTranslation();
  const [detailsReady, setDetailsReady] = useState(false);
  const [chartsSettling, setChartsSettling] = useState(false);
  const countrySection = p145Section(report, ['country', 'countries']);
  const countryRows = p145Rows(countrySection);
  const weekLabel = mode === 'monthly'
    ? (monthNames[Number(filters?.month || meta?.month)] || String(filters?.month || meta?.month || ''))
    : (filters?.week || meta?.week || '');
  const trendEyebrow = mode === 'monthly'
    ? (() => {
        const m = Number(filters?.month || meta?.month);
        if (!m) return t('xSales.withAch');
        const start = monthNames[1];
        const end = monthNames[m] || String(m);
        return t('xSales.withAchPeriod', { period: m === 1 ? end : `${start}–${end}` });
      })()
    : t('xSales.withAchPeriod', { period: weekLabel });
  const chartResetKey = `${mode}-${weekLabel}-${symbol}-${report?.meta?.year || meta?.year || ''}-${filters?.currency || ''}`;
  const chartsLoading = loadingReport || chartsSettling;
  const trendLoading = loadingReport || loadingVisualHistory || chartsSettling;

  useEffect(() => {
    setDetailsReady(false);
    setChartsSettling(true);
    const detailTimer = window.setTimeout(() => setDetailsReady(true), 180);
    const settleTimer = window.setTimeout(() => setChartsSettling(false), 2200);
    return () => {
      window.clearTimeout(detailTimer);
      window.clearTimeout(settleTimer);
    };
  }, [report, mode, weekLabel, symbol]);

  return (
    <div className="srd-v145-layout c150-charts-layout">

      {/* Row 1: Weekly trend + Actual vs Budget */}
      <P145Card
        title={mode === 'monthly' ? t('xSales.cardTrendMonthly') : t('xSales.cardTrendWeekly')}
        eyebrow={trendEyebrow}
        className="span-2 srd-v145-trend-card"
        loading={trendLoading}
        loadingLabel={t('xSales.loadingShort')}
      >
        <C150WeeklyTrend total={total} mode={mode} meta={meta} symbol={symbol} history={visualHistory} targetVisible={targetVisible} loading={trendLoading} />
      </P145Card>
      <P145Card title={t('xSales.cardActualVsTarget')} eyebrow={t('xSales.varianceDetail')} badge={weekLabel}>
        <P145ActualBudget total={total} mode={mode} symbol={symbol} targetVisible={targetVisible} />
      </P145Card>

      {!detailsReady ? (
        <>
          <div className="c150-side-by-side">
            <P145Card title={t('xSales.cardEntity')} eyebrow={t('xSales.breakdownEyebrow')} badge={weekLabel} loading>
              <P145ChartLoading />
            </P145Card>
            <P145Card title={t('xSales.cardChannel')} eyebrow={t('xSales.breakdownEyebrow')} badge={weekLabel} loading>
              <P145ChartLoading />
            </P145Card>
          </div>
          <P145Card title={t('xSales.topCustomers', { n: Math.min(10, customerRows.length) })} eyebrow={weekLabel} loading>
            <P145ChartLoading />
          </P145Card>
          <P145Card title={skuRows.length ? t('xSales.topSkus', { n: Math.min(10, skuRows.length) }) : t('xSales.topLines', { n: Math.min(10, lineRows.length) })} eyebrow={weekLabel} loading>
            <P145ChartLoading />
          </P145Card>
          <P145Card title={t('xSales.cardCountryMap')} eyebrow={weekLabel} className="c150-map-card" loading loadingLabel={t('xSales.loadingMapShort')}>
            <P145ChartLoading label={t('xSales.loadingMap')} />
          </P145Card>
          <P145Card title={t('xSales.cardLineMix')} eyebrow={t('xSales.pctOfRevenue')} badge={weekLabel} className="span-1" loading>
            <P145ChartLoading />
          </P145Card>
          <P145Card title={t('xSales.cardCategoryMix')} eyebrow={t('xSales.pctOfRevenue')} badge={weekLabel} className="span-1" loading>
            <P145ChartLoading />
          </P145Card>
          <P145Card title={t('xSales.cardTypeMix')} eyebrow={t('xSales.pctOfRevenue')} badge={weekLabel} className="span-1" loading>
            <P145ChartLoading />
          </P145Card>
        </>
      ) : (
        <>
          {/* Row 2: Entity + Channel breakdown side by side */}
          <div className="c150-side-by-side">
            <P145Card title={t('xSales.cardEntity')} eyebrow={t('xSales.breakdownEyebrow')} badge={weekLabel} loading={chartsLoading}>
              <P145ChartFrame loading={chartsLoading} resetKey={`${chartResetKey}-entity`}>
                <C150EntityChart rows={entityRows} mode={mode} symbol={symbol} targetVisible={targetVisible} />
              </P145ChartFrame>
            </P145Card>
            <P145Card title={t('xSales.cardChannel')} eyebrow={t('xSales.breakdownEyebrow')} badge={weekLabel} loading={chartsLoading}>
              <P145ChartFrame loading={chartsLoading} resetKey={`${chartResetKey}-channel`}>
                <C150ChannelChart rows={channelRows} mode={mode} symbol={symbol} targetVisible={targetVisible} />
              </P145ChartFrame>
            </P145Card>
          </div>

          {/* Row 4: Top 10 customer | Top 10 SKU/Line | Country bubble map */}
          <P145Card title={t('xSales.topCustomers', { n: Math.min(10, customerRows.length) })} eyebrow={weekLabel} loading={chartsLoading}>
            <P145ChartFrame loading={chartsLoading} resetKey={`${chartResetKey}-customers`}>
              <C150HorizBar rows={customerRows} mode={mode} symbol={symbol} limit={10} color="#009FE3" />
            </P145ChartFrame>
          </P145Card>
          <P145Card title={skuRows.length ? t('xSales.topSkus', { n: Math.min(10, skuRows.length) }) : t('xSales.topLines', { n: Math.min(10, lineRows.length) })} eyebrow={weekLabel} loading={chartsLoading}>
            <P145ChartFrame loading={chartsLoading} resetKey={`${chartResetKey}-sku-line`}>
              <C150HorizBar rows={skuRows.length ? skuRows : lineRows} mode={mode} symbol={symbol} limit={10} color="#009FE3" />
            </P145ChartFrame>
          </P145Card>
          <P145Card title={t('xSales.cardCountryMap')} eyebrow={weekLabel} className="c150-map-card" loading={chartsLoading} loadingLabel={t('xSales.loadingMapShort')}>
            <P145ChartFrame loading={chartsLoading} label={t('xSales.loadingMap')} resetKey={`${chartResetKey}-map`}>
              <C150BubbleMap rows={countryRows} mode={mode} symbol={symbol} />
            </P145ChartFrame>
          </P145Card>

          {/* Row 6: Pie charts for product line, category, and type */}
          <P145Card title={t('xSales.cardLineMix')} eyebrow={t('xSales.pctOfRevenue')} badge={weekLabel} className="span-1" loading={chartsLoading}>
            <P145ChartFrame loading={chartsLoading} resetKey={`${chartResetKey}-line-pie`}>
              <C150PieChart rows={lineRows} mode={mode} symbol={symbol} title="" />
            </P145ChartFrame>
          </P145Card>
          <P145Card title={t('xSales.cardCategoryMix')} eyebrow={t('xSales.pctOfRevenue')} badge={weekLabel} className="span-1" loading={chartsLoading}>
            <P145ChartFrame loading={chartsLoading} resetKey={`${chartResetKey}-category-pie`}>
              <C150PieChart rows={productRows.length ? productRows : lineRows} mode={mode} symbol={symbol} title="" />
            </P145ChartFrame>
          </P145Card>
          <P145Card title={t('xSales.cardTypeMix')} eyebrow={t('xSales.pctOfRevenue')} badge={weekLabel} className="span-1" loading={chartsLoading}>
            <P145ChartFrame loading={chartsLoading} resetKey={`${chartResetKey}-type-pie`}>
              <C150PieChart rows={productTypeRows.length ? productTypeRows : lineRows} mode={mode} symbol={symbol} title="" />
            </P145ChartFrame>
          </P145Card>
        </>
      )}

    </div>
  );
};
// Phase150 Charts tab - END

// Phase145 report-specific visual analytics - END


// Visual history hook - fetches a small trend window with session-level cache.
const useVisualHistory = (filters, availableWeeks) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const cache = useRef(new Map());
  const activeLoadRef = useRef(0);
  const lastLoadKeyRef = useRef('');
  const historyRef = useRef([]);
  const setNextHistory = useCallback((nextHistory) => {
    historyRef.current = nextHistory;
    setHistory(nextHistory);
  }, []);

  const load = useCallback(async (currentReport) => {
    if (!currentReport) {
      setNextHistory([]);
      return;
    }

    const filterKey = JSON.stringify({
      entities: filters.entities,
      channels: filters.channels,
      channel2s: filters.channel2s,
      customerNames: filters.customerNames,
      skus: filters.skus,
      lines: filters.lines,
      regions: filters.regions,
      subregions: filters.subregions,
      countries: filters.countries,
      excludeDistributor: filters.excludeDistributor,
      includeOversea: filters.includeOversea,
      includeInternal: filters.includeInternal,
      currency: filters.currency,
    });
    const reportKey = `${filters.reportType}-${filters.year}-${filters.month}-${filters.week}-${filterKey}`;
    if (lastLoadKeyRef.current === reportKey && historyRef.current.length) return;
    lastLoadKeyRef.current = reportKey;
    const loadId = activeLoadRef.current + 1;
    activeLoadRef.current = loadId;

    if (isWeeklyReportType(filters.reportType)) {
      const sortedWeeks = (availableWeeks || [])
        .slice()
        .sort((a, b) => (Number(a.year) - Number(b.year)) || (p145WeekNumber(a.value) - p145WeekNumber(b.value)));
      const selectedIndex = sortedWeeks.findIndex((w) => String(w.value) === String(filters.week));
      if (selectedIndex < 0) { setNextHistory([]); return; }

      const weeks = sortedWeeks.slice(Math.max(0, selectedIndex - VISUAL_WEEK_HISTORY_COUNT), selectedIndex + 1);
      const currentPoint = p145HistoryPointFromReport(currentReport, { year: filters.year, week: filters.week });
      const seededWeeklyPoints = p145SeedWeeklyHistoryFromReport(currentReport, { year: filters.year, week: filters.week });
      setNextHistory(seededWeeklyPoints);
      if (weeks.length <= 1) return;

      setLoading(true);
      try {
        const cacheKey = `weekly-batch-${weeks.map((week) => `${week.year || filters.year}:${week.value}`).join('|')}-${filterKey}`;
        const results = cache.current.has(cacheKey)
          ? cache.current.get(cacheKey)
          : (await fetchSalesReportVisualHistory({
              ...filters,
              reportType: 'weekly',
              month: 'All',
              points: weeks.map((week) => ({
                year: week.year || filters.year,
                week: p145WeekNumber(week.value),
                label: week.value,
              })),
            })).points || [];
        cache.current.set(cacheKey, results);
        if (activeLoadRef.current === loadId) {
          const withCurrent = results.map((point) => (
            String(point.label) === String(filters.week) ? { ...point, ...currentPoint } : point
          ));
          setNextHistory(p145MergeHistoryPoints(withCurrent).sort((a, b) => p145WeekNumber(a.label) - p145WeekNumber(b.label)));
        }
      } catch (err) {
        console.error('[sales report visual history]', err);
        if (activeLoadRef.current === loadId) {
          setNextHistory(seededWeeklyPoints);
        }
      } finally {
        if (activeLoadRef.current === loadId) {
          setLoading(false);
        }
      }

    } else if (isMonthlyReportType(filters.reportType)) {
      const currentMonth = Number(filters.month);
      if (!currentMonth || currentMonth < 1) { setNextHistory([]); return; }

      const months = Array.from({ length: currentMonth }, (_, i) => i + 1);
      const currentPoint = p145HistoryPointFromReport(currentReport, { year: filters.year, month: currentMonth }, 'monthly');
      setNextHistory([currentPoint].filter((item) => item.value || item.budget));
      if (months.length <= 1) return;

      setLoading(true);
      try {
        const seededMonthlyPoints = [currentPoint].filter((item) => item.value || item.budget);
        const cacheKey = `monthly-batch-${filters.year}-${months.join('|')}-${filterKey}`;
        const results = cache.current.has(cacheKey)
          ? cache.current.get(cacheKey)
          : (await fetchSalesReportVisualHistory({
              ...filters,
              reportType: 'monthly',
              year: filters.year,
              week: 'All',
              points: months.map((month) => ({
                year: filters.year,
                month,
                label: monthNames[month] || String(month),
              })),
            })).points || [];
        cache.current.set(cacheKey, results);
        if (activeLoadRef.current === loadId) {
          const withCurrent = results.map((point) => (
            Number(point.month) === currentMonth ? { ...point, ...currentPoint } : point
          ));
          setNextHistory(p145MergeHistoryPoints([...seededMonthlyPoints, ...withCurrent]).sort((a, b) => {
            const ma = Number(Object.entries(monthNames).find(([, name]) => name === a.label)?.[0] || 0);
            const mb = Number(Object.entries(monthNames).find(([, name]) => name === b.label)?.[0] || 0);
            return ma - mb;
          }));
        }
      } catch (err) {
        console.error('[sales report monthly visual history]', err);
        if (activeLoadRef.current === loadId) {
          setNextHistory([currentPoint]);
        }
      } finally {
        if (activeLoadRef.current === loadId) {
          setLoading(false);
        }
      }

    } else {
      setNextHistory([]);
    }
  }, [filters, availableWeeks, setNextHistory]);

  return { history, loading, load };
};

/* THE GUIDE (Max 01/11: every module has one). Business Performance is one screen with several reports behind the Report
   picker; the steps describe the controls every report shares. The period filters are not drawn on the Inventory /
   Marketing / Pole pages, so their step is skipped there. */
const TOUR = [
  { selector: '.srd-filters .srd-field:first-child', title: 'tourBizPerf.reportT', body: 'tourBizPerf.reportB' },
  { selector: '[data-tour="bp-period"]', title: 'tourBizPerf.periodT', body: 'tourBizPerf.periodB' },
  { selector: '.srd-filters .srd-check', title: 'tourBizPerf.optionsT', body: 'tourBizPerf.optionsB' },
  { selector: '.srd-actions', title: 'tourBizPerf.actionsT', body: 'tourBizPerf.actionsB' },
  { selector: '.srd-summary', title: 'tourBizPerf.tablesT', body: 'tourBizPerf.tablesB' },
];

function SalesReportDashboard() {
  const { permissions } = useAuth();
  const { t } = useTranslation();
  const theme = 'light';
  const [meta, setMeta] = useState(null);
  const [filterMeta, setFilterMeta] = useState(null);
  const [report, setReport] = useState(null);
  /* the body the shown report was asked with — the AI summary asks the server for the same report */
  const [insightBody, setInsightBody] = useState(null);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const [error, setError] = useState('');
  const [autoRun, setAutoRun] = useState(true);
  const [showVisualAnalytics, setShowVisualAnalytics] = useState(false);
  const reportScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const syncingScrollRef = useRef(false);
  const reportCacheRef = useRef(new Map());
  const reportRequestRef = useRef({ id: 0, controller: null });
  const [reportScrollWidth, setReportScrollWidth] = useState(0);

  const [filters, setFilters] = useState({
    reportType: 'weekly',
    year: '',
    month: 'All',
    week: '',
    quarter: 'All',
    currency: 'KRW',
    entities: [],
    channels: [],
    channel2s: [],
    customerNames: [],
    skus: [],
    lines: [],
    regions: [],
    subregions: [],
    countries: [],
    excludeDistributor: false,
    includeOversea: true,
    includeInternal: false,
  });

  const weekOptions = React.useMemo(() => {
    const allWeeks = filterMeta?.weeks || [];
    const selectedYear = Number(filters.year);
    return allWeeks.filter((week) => !week.year || Number(week.year) === selectedYear);
  }, [filterMeta, filters.year]);

  React.useEffect(() => {
    if (filters.reportType !== 'weekly' || !weekOptions.length) return;
    if (!weekOptions.some((week) => week.value === filters.week)) {
      setFilters((current) => ({ ...current, week: weekOptions[0].value }));
    }
  }, [filters.reportType, filters.week, weekOptions]);


  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoadingFilters(true);
        const response = await fetch(`${API_BASE}/api/sales-report/filters`, { credentials: 'include' });
        if (!response.ok) throw new Error(i18n.t('xSales.errLoadFilters'));
        const data = normalizeSalesReportFilterMeta(await response.json());
        setFilterMeta(data);
        setFilters((current) => ({
          ...current,
          year: data.defaults?.weeklyYear || data.defaults?.year || data.years?.[0] || '',
          month: isWeeklyReportType(current.reportType) ? 'All' : data.defaults?.monthlyMonth || data.defaults?.month || '',
          week: isWeeklyReport2Type(current.reportType) ? 'All' : data.defaults?.weeklyWeek || data.defaults?.week || data.weeks?.[0]?.value || '',
          quarter: current.quarter || 'All',
          // Keep EUR if the user already switched to Weekly Flash while this request was in flight,
          // otherwise the late response would snap the currency back to the server default.
          currency: current.reportType === 'weeklyFlash'
            ? 'EUR'
            : (data.defaults?.currency || 'KRW'),
        }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingFilters(false);
      }
    };
    loadFilters();
  }, []);

  const availableWeeks = useMemo(() => {
    const weeks = filterMeta?.weeks || [];
    return weeks.filter((week) => !filters.year || Number(week.year) === Number(filters.year));
  }, [filterMeta, filters.year]);

  const { history: visualHistory, loading: loadingVisualHistory, load: loadVisualHistory } = useVisualHistory(filters, availableWeeks);

  useEffect(() => {
    const updateScrollWidth = () => {
      if (!reportScrollRef.current) return;
      setReportScrollWidth(reportScrollRef.current.scrollWidth);
      if (topScrollRef.current) {
        topScrollRef.current.scrollLeft = reportScrollRef.current.scrollLeft;
      }
    };
    updateScrollWidth();
    window.addEventListener('resize', updateScrollWidth);
    const timer = window.setTimeout(updateScrollWidth, 250);
    return () => {
      window.removeEventListener('resize', updateScrollWidth);
      window.clearTimeout(timer);
    };
  }, [report, meta, filters.reportType, loadingReport]);

  const syncHorizontalScroll = useCallback((source) => {
    if (syncingScrollRef.current) return;
    const from = source === 'top' ? topScrollRef.current : reportScrollRef.current;
    const to = source === 'top' ? reportScrollRef.current : topScrollRef.current;
    if (!from || !to) return;
    syncingScrollRef.current = true;
    to.scrollLeft = from.scrollLeft;
    window.requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  }, []);

  const resetReportTableScroll = useCallback(() => {
    const reset = () => {
      syncingScrollRef.current = true;
      if (topScrollRef.current) topScrollRef.current.scrollLeft = 0;
      if (reportScrollRef.current) reportScrollRef.current.scrollLeft = 0;
      document.querySelectorAll('.srd-table-wrap, .srd-weekly2-table-wrap, .srd-weekly2-summary-wrap').forEach((element) => {
        element.scrollLeft = 0;
      });
      window.requestAnimationFrame(() => {
        syncingScrollRef.current = false;
      });
    };

    window.requestAnimationFrame(reset);
    window.setTimeout(reset, 0);
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => {
      const next = { ...current, [key]: value };
      if (key === 'reportType' && isWeeklyReportType(value)) {
        const nextYear = filterMeta?.defaults?.weeklyYear || filterMeta?.defaults?.year || current.year;
        const nextWeek = filterMeta?.defaults?.weeklyWeek || filterMeta?.defaults?.week || current.week;
        next.year = nextYear;
        next.week = isWeeklyReport2Type(value) ? 'All' : nextWeek;
        next.month = 'All';
        if (isWeeklyReport2Type(value)) {
          Object.assign(next, WEEKLY2_INACTIVE_FILTERS);
          next.quarter = current.quarter || 'All';
        }
      }
      if (key === 'reportType' && isMonthlyReportType(value)) {
        next.year = filterMeta?.defaults?.monthlyYear || filterMeta?.defaults?.year || current.year;
        next.month = filterMeta?.defaults?.monthlyMonth || filterMeta?.defaults?.month || current.month;
        next.week = value === 'weeklyFlash'
          ? (filterMeta?.defaults?.weeklyWeek || filterMeta?.defaults?.week || current.week || 'All')
          : 'All';
        next.quarter = 'All';
        if (value === 'weeklyFlash') {
          next.entities = ['HEMA'];
          next.excludeDistributor = true;
          // The Weekly Flash Report is read in EUR, so switching to it switches the currency too.
          // Left as a default rather than a lock — the dropdown still works if someone wants KRW.
          next.currency = 'EUR';
        }
      }
      if (key === 'year' && current.reportType === 'weekly') {
        const maxWeek = (filterMeta?.weeks || []).find((week) => Number(week.year) === Number(value));
        next.week = maxWeek?.value || current.week;
      }
      return next;
    });
  }, [filterMeta]);

  const hasActiveFilters = useMemo(() => filters.entities.length > 0 || filters.channels.length > 0
    || filters.channel2s.length > 0 || filters.customerNames.length > 0 || filters.skus.length > 0 || filters.lines.length > 0
    || filters.regions.length > 0 || filters.subregions.length > 0 || filters.countries.length > 0
    || filters.excludeDistributor || !filters.includeOversea || filters.includeInternal, [filters]);

  const clearFilters = useCallback(() => {
    setFilters((current) => ({
      ...current,
      entities: current.reportType === 'weeklyFlash' ? ['HEMA'] : [],
      channels: [],
      channel2s: [],
    customerNames: [],
    skus: [],
    lines: [],
      regions: [],
      subregions: [],
      countries: [],
      excludeDistributor: current.reportType === 'weeklyFlash',
      includeOversea: true,
      includeInternal: false,
    }));
  }, []);

  const loadReport = useCallback(async () => {
    /* the Inventory and Marketing pages read their own endpoints (BizTables.jsx) — nothing of the sales report */
    if (BIZ_PAGES.includes(filters.reportType)) return;
    reportRequestRef.current.controller?.abort();
    const requestId = reportRequestRef.current.id + 1;
    reportRequestRef.current = { id: requestId, controller: null };
    const isActiveRequest = () => reportRequestRef.current.id === requestId;
    const requestFilters = normalizeWeekly2Filters(filters);
    setInsightBody({ ...requestFilters, reportType: apiReportType(filters.reportType), week: filters.reportType === 'weeklyFlash' ? 'All' : requestFilters.week, page: filters.reportType });

    const cacheKey = salesReportCacheKey(requestFilters);
    const cachedReport = reportCacheRef.current.get(cacheKey);
    if (cachedReport) {
      const preparedCachedReport = phase148AddYearProjectedAchievement(cachedReport);
      setError('');
      setReport(preparedCachedReport);
      setMeta(preparedCachedReport.meta);
      resetReportTableScroll();
      return;
    }

    const controller = new AbortController();
    reportRequestRef.current = { id: requestId, controller };

    try {
      setError('');
      setLoadingReport(true);
      resetReportTableScroll();
      const endpoint = apiReportType(filters.reportType);
      const data = await fetchSalesReportJson(endpoint, {
        ...requestFilters,
        reportType: endpoint,
        week: filters.reportType === 'weeklyFlash' ? 'All' : requestFilters.week,
      }, controller.signal);
      if (!isActiveRequest()) return;

      const needsMonthlySnapshot = (
        filters.reportType === 'weekly'
        && data?.meta?.targetMonth
        && !salesReportHasDetailedTargetlessFilter(filters)
      );

      if (needsMonthlySnapshot) {
        const baseReport = phase148PrepareReport({
          ...data,
          meta: {
            ...(data.meta || {}),
            monthSnapshotLoading: true,
          },
        });
        setReport(baseReport);
        setMeta(baseReport.meta);
        setLoadingReport(false);
        resetReportTableScroll();

        const snapshotController = new AbortController();
        let snapshotTimedOut = false;
        const abortSnapshot = () => snapshotController.abort();
        controller.signal.addEventListener('abort', abortSnapshot, { once: true });
        const snapshotTimeout = window.setTimeout(() => {
          snapshotTimedOut = true;
          snapshotController.abort();
        }, 15000);

        try {
          const monthlyData = await fetchSalesReportJson('monthly', {
            ...filters,
            reportType: 'monthly',
            month: data.meta.targetMonth,
            week: 'All',
            snapshotWeek: filters.week,
          }, snapshotController.signal);
          if (!isActiveRequest()) return;
          const finalReport = phase148PrepareReport(phase125MergeWeeklyMonthSnapshot(data, monthlyData));
          rememberSalesReport(reportCacheRef.current, cacheKey, finalReport);
          setReport(finalReport);
          setMeta(finalReport.meta);
          resetReportTableScroll();
        } catch (snapshotError) {
          if ((isAbortError(snapshotError) && !snapshotTimedOut) || !isActiveRequest()) return;
          console.error('[weekly month snapshot] request failed', snapshotError);
          const finalReport = phase148PrepareReport({
            ...data,
            meta: {
              ...(data.meta || {}),
              monthSnapshotError: snapshotTimedOut
                ? i18n.t('xSales.snapshotTimeout')
                : (snapshotError.message || i18n.t('xSales.snapshotFailed')),
            },
          });
          rememberSalesReport(reportCacheRef.current, cacheKey, finalReport);
          setReport(finalReport);
          setMeta(finalReport.meta);
          resetReportTableScroll();
        } finally {
          window.clearTimeout(snapshotTimeout);
          controller.signal.removeEventListener('abort', abortSnapshot);
        }
        return;
      }

      let finalData = data;
      if (filters.reportType === 'weeklyFlash') {
        try {
          const weeklyWeek = (filters.week && filters.week !== 'All' ? filters.week : null)
            || filterMeta?.defaults?.weeklyWeek
            || filterMeta?.defaults?.week
            || filterMeta?.weeks?.[0]?.value
            || 'All';
          const weeklyData = await fetchSalesReportJson('weekly', {
            ...requestFilters,
            reportType: 'weekly',
            month: 'All',
            week: weeklyWeek,
            quarter: 'All',
          }, controller.signal);
          if (!isActiveRequest()) return;
          const breakoutEntities = weeklyFlashWholesaleBreakouts.map((breakout) => breakout.entity);
          let breakoutEntries = weeklyFlashWholesaleBreakouts.map((breakout) => [breakout.key, { row: null, weeklyRow: null }]);
          try {
            const [monthlyBreakoutData, weeklyBreakoutData] = await Promise.all([
              fetchSalesReportJson(
                'monthly',
                buildWeeklyFlashBreakoutFilters(requestFilters, breakoutEntities, 'monthly', weeklyWeek),
                controller.signal,
              ),
              fetchSalesReportJson(
                'weekly',
                buildWeeklyFlashBreakoutFilters(requestFilters, breakoutEntities, 'weekly', weeklyWeek),
                controller.signal,
              ),
            ]);
            const monthlyBreakoutReport = phase148PrepareReport(monthlyBreakoutData);
            const weeklyBreakoutReport = phase148PrepareReport(weeklyBreakoutData);
            breakoutEntries = weeklyFlashWholesaleBreakouts.map((breakout) => [breakout.key, {
              row: flashEntityRow(monthlyBreakoutReport, breakout.names),
              weeklyRow: flashEntityRow(weeklyBreakoutReport, breakout.names),
            }]);
          } catch (breakoutError) {
            if (isAbortError(breakoutError)) throw breakoutError;
            console.error('[weekly flash wholesale breakouts] request failed', breakoutError);
            breakoutEntries = weeklyFlashWholesaleBreakouts.map((breakout) => [breakout.key, {
              row: null,
              weeklyRow: null,
              error: breakoutError.message || 'Wholesale breakout request failed',
            }]);
          }
          if (!isActiveRequest()) return;
          finalData = {
            ...data,
            weeklyFlashWeeklyReport: phase148PrepareReport(weeklyData),
            weeklyFlashBreakouts: Object.fromEntries(breakoutEntries),
            meta: {
              ...(data.meta || {}),
              weeklyFlashWeek: weeklyWeek,
            },
          };
        } catch (weeklyFlashError) {
          if (isAbortError(weeklyFlashError) || !isActiveRequest()) return;
          console.error('[weekly flash weekly snapshot] request failed', weeklyFlashError);
          finalData = {
            ...data,
            meta: {
              ...(data.meta || {}),
              weeklyFlashError: weeklyFlashError.message || 'Weekly snapshot request failed',
            },
          };
        }
      }

      const finalReport = phase148PrepareReport(finalData);
      rememberSalesReport(reportCacheRef.current, cacheKey, finalReport);
      setReport(finalReport);
      setMeta(finalReport.meta);
      resetReportTableScroll();
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err.message);
    } finally {
      if (isActiveRequest()) {
        setLoadingReport(false);
      }
    }
  }, [filters, filterMeta, resetReportTableScroll]);

  useEffect(() => {
    if (!showVisualAnalytics || !report) return;
    loadVisualHistory(report);
  }, [showVisualAnalytics, report, loadVisualHistory]);

  useEffect(() => {
    if (!autoRun || loadingFilters) return undefined;
    const handle = setTimeout(loadReport, 500);
    return () => clearTimeout(handle);
  }, [autoRun, loadingFilters, loadReport]);

  useEffect(() => () => {
    reportRequestRef.current.controller?.abort();
  }, []);

  /**
   * The body both exports post. Built once so the spreadsheet can never drift from the PDF: same sections,
   * same weekly2 row flattening, same visible column set, same filters. Whatever the filter bar is set to
   * is already inside `report`, so neither export re-queries anything.
   */
  const buildExportPayload = useCallback(() => {
    const currentMeta = report.meta || meta || {};
    const pdfReport = isWeeklyReport2Type(currentMeta.reportType)
      ? {
          ...report,
          sections: (report.sections || []).map((section) => {
            if (section.key === 'weeklyGlobal') {
              return { ...section, rows: weekly2DisplayRows(section, 80) };
            }
            if (section.key === 'productCategoryTree') {
              return { ...section, rows: weekly2DisplayRows(section, 120) };
            }
            if (section.key === 'line') {
              return { ...section, rows: weekly2DisplayRows(section, 40) };
            }
            return section;
          }),
        }
      : report;
    // The PDF keeps English headings whatever the screen's language: the server draws it in DejaVu Sans, which
    // has no Hangul or Han glyphs, so a Korean or Chinese heading would print as empty boxes.
    const englishT = i18n.getFixedT('en');
    const exportColumns = salesReportVisibleColumns(
      isMonthlyReportType(currentMeta.reportType) ? monthlyColumns(currentMeta, englishT) : weeklyColumns(currentMeta, englishT),
      filters,
    );
    return {
      fileName: buildExportFileName(filters, currentMeta),
      filters,
      report: pdfReport,
      columns: phase135PdfColumns(exportColumns),
    };
  }, [filters, meta, report]);

  const runExport = useCallback(async (path, extension, setBusy, label) => {
    if (!report) return;
    try {
      setError('');
      setBusy(true);
      const payload = buildExportPayload();
      // buildExportFileName ends in .pdf; the Excel route asks for the same name with the right extension
      // so a PDF and a spreadsheet of the same view sit next to each other in the download folder.
      const fileName = payload.fileName.replace(/\.pdf$/i, extension);
      const response = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, fileName }),
      });

      if (!response.ok) {
        let message = i18n.t('xSales.errExport', { what: label });
        try {
          const data = await response.json();
          message = data.detail || data.error || message;
        } catch (ignore) {
          // Keep the default message when the server does not return JSON.
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 500);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [buildExportPayload, report]);

  const exportPdf = useCallback(
    () => runExport('/api/sales-report/export-pdf', '.pdf', setExportingPdf, 'PDF'),
    [runExport],
  );
  // The flat transaction rows, not the aggregated report tables: Max works from a fact table in the shape of
  // the Append1 sheet in HPAC.xlsm, which is what his pivots are built against. Three sheets — Year, Month,
  // Week — for the periods on the filter bar.
  const exportXlsx = useCallback(
    () => runExport('/api/sales-report/export-rows-xlsx', '.xlsx', setExportingXlsx, 'Excel'),
    [runExport],
  );

  const columns = useMemo(() => salesReportVisibleColumns(
    isMonthlyReportType(meta?.reportType) ? monthlyColumns(meta, t) : weeklyColumns(meta || {}, t),
    filters,
  ), [meta, filters, t]);

  useEffect(() => {
    if (!report?.sections?.length) return undefined;

    const wraps = Array.from(document.querySelectorAll('.srd-table-wrap, .srd-weekly2-table-wrap, .srd-weekly2-summary-wrap'));
    if (wraps.length < 2) return undefined;

    let syncing = false;
    const salesReportSyncTableHorizontalScroll = (event) => {
      if (syncing) return;
      syncing = true;
      const source = event.currentTarget;
      const nextLeft = source.scrollLeft;
      wraps.forEach((wrap) => {
        if (wrap !== source && Math.abs(wrap.scrollLeft - nextLeft) > 1) {
          wrap.scrollLeft = nextLeft;
        }
      });
      window.requestAnimationFrame(() => {
        syncing = false;
      });
    };

    wraps.forEach((wrap) => wrap.addEventListener('scroll', salesReportSyncTableHorizontalScroll, { passive: true }));
    return () => {
      wraps.forEach((wrap) => wrap.removeEventListener('scroll', salesReportSyncTableHorizontalScroll));
    };
  }, [report]);

  // phase27 reset table horizontal scroll after report refresh
  useEffect(() => {
    if (!report) return undefined;
    const frame = window.requestAnimationFrame(resetReportTableScroll);
    return () => window.cancelAnimationFrame(frame);
  }, [report, resetReportTableScroll]);

  // A run of facts joined by " / " — each piece is translated on its own; 'All' is the filter VALUE and is only
  // translated where it is printed.
  const subtitle = useMemo(() => {
    const shownAll = (value) => (value === 'All' ? t('xSales.all') : value);
    const entityText = selectedText(filters.entities, t('xSales.fEntity'));
    const detailText = [
      entityText,
      selectedText(filters.channels, t('xSales.fChannel')),
      selectedText(filters.customerNames, t('xSales.fCustomerName')),
      selectedText(filters.skus, 'SKU'),
    ].join(' / ');
    return filters.reportType === 'weeklyFlash'
      ? `${shownAll(meta?.weeklyFlashWeek || filters.week || 'All')} / ${t('xSales.monthN', { month: meta.month })} / YTD / ${meta.year} / ${meta.currency} / ${detailText}`
    : isMonthlyReportType(filters.reportType)
      ? `${t('xSales.monthN', { month: meta.month })} / YTD / ${meta.year} / ${meta.currency} / ${detailText}`
      : isWeeklyReport2Type(filters.reportType)
        ? `${shownAll(filters.quarter || 'All')} / ${filters.year} / ${filters.currency} / ${entityText}`
      : `${meta?.week || filters.week} / ${meta?.prevWeek1 || ''} / ${meta?.prevWeek2 || ''} / ${filters.year} / ${filters.currency} / ${detailText}`;
  }, [meta, filters, t]);
  const weekly2Mode = isWeeklyReport2Type(filters.reportType);
  // The Weekly Flash Report is built entirely around HEMA: every block in flashBlockConfigs looks
  // for HEMA / HEMA_EAST / HEMA_WEST rows, and weeklyFlashWholesaleBreakouts requests those two
  // entities outright. Picking another entity leaves the main blocks empty while the East/West
  // breakouts still return HEMA, i.e. a mixed report that reads as real. So the Entity filter is
  // pinned to HEMA and locked for this report type.
  const flashMode = filters.reportType === 'weeklyFlash';
  const bizPage = BIZ_PAGES.includes(filters.reportType);
  const weekFilterDisabled = (filters.reportType === 'monthly') || weekly2Mode;
  const inactiveWeekly2DropdownValue = [];

  return (
    <div className={`srd-page ${theme}`} data-report-type={filters.reportType}>
      <Header />
      <main className="srd-main">
        <section className="srd-controls">
          <div className="srd-titlebar">
            <h1>{t('xSales.title')}</h1>
            <ModuleHelp steps={TOUR} tourKey="businessPerformance" ready={!loadingFilters} />
          </div>

          <div className="srd-filters">
            <label className="srd-field">
              <span>{t('xSales.fReport')}</span>
              <select value={filters.reportType} onChange={(event) => updateFilter('reportType', event.target.value)}>
                <option value="weekly">{t('xSales.reportWeekly')}</option>
                <option value="weekly2">{t('xSales.reportWeekly2')}</option>
                <option value="monthly">{t('xSales.reportMonthly')}</option>
                <option value="weeklyFlash">{t('xSales.reportWeeklyFlash')}</option>
                {BP_TABLES_ON && (permissions.inventory_dashboard || permissions.inventory_report)
                  ? <option value="inventory">{t('xBiz.reportInventory')}</option> : null}
                {BP_TABLES_ON && permissions.marketing_dashboard ? <option value="marketing">{t('xBiz.reportMarketing')}</option> : null}
                {BP_TABLES_ON && (permissions.pole_inventory || permissions.inventory_dashboard || permissions.inventory_report)
                  ? <option value="pole">{t('xBiz.reportPole')}</option> : null}
              </select>
            </label>
            {!bizPage && (<>
            <label className="srd-field" data-tour="bp-period">
              <span>{t('xSales.fYear')}</span>
              <select value={filters.year} onChange={(event) => updateFilter('year', event.target.value)}>
                {(filterMeta?.years || []).map((year) => <option key={year} value={year}>{year}</option>)}
              </select>
            </label>
            <label className="srd-field">
              <span>{t('xSales.fMonth')}</span>
              <select
                value={isWeeklyReportType(filters.reportType) ? 'All' : filters.month}
                disabled={isWeeklyReportType(filters.reportType)}
                onChange={(event) => updateFilter('month', event.target.value)}
              >
                {isWeeklyReportType(filters.reportType) && <option value="All">{t('xSales.all')}</option>}
                {(filterMeta?.months || []).map((month) => <option key={month} value={month}>{monthNames[month] || month}</option>)}
              </select>
            </label>
            <label className="srd-field">
              <span>{t('xSales.fQuarter')}</span>
              <select
                value={weekly2Mode ? filters.quarter || 'All' : 'All'}
                disabled={!weekly2Mode}
                onChange={(event) => updateFilter('quarter', event.target.value)}
              >
                {weekly2QuarterOptions.map((quarter) => <option key={quarter.value} value={quarter.value}>{quarter.value === 'All' ? t('xSales.all') : quarter.label}</option>)}
              </select>
            </label>
            <label className="srd-field">
              <span>{t('xSales.fWeek')}</span>
              <select
                value={weekFilterDisabled ? 'All' : filters.week}
                disabled={weekFilterDisabled}
                onChange={(event) => updateFilter('week', event.target.value)}
              >
                {(weekFilterDisabled || filters.reportType === 'weeklyFlash') && <option value="All">{t('xSales.all')}</option>}
                {availableWeeks.map((week) => <option key={`${week.year}-${week.value}`} value={week.value}>{week.value}</option>)}
              </select>
            </label>
            <label className="srd-field">
              <span>{t('xSales.fCurrency')}</span>
              <select value={filters.currency} onChange={(event) => updateFilter('currency', event.target.value)}>
                {(filterMeta?.currencies || ['KRW', 'USD', 'EUR', 'JPY', 'SGD']).map((currency) => <option key={currency} value={currency}>{currency}</option>)}
              </select>
            </label>
            <CheckboxDropdown label={t('xSales.fEntity')} options={filterMeta?.entities || []} value={flashMode ? ['HEMA'] : filters.entities} onChange={(value) => updateFilter('entities', value)} disabled={flashMode} />
            <CheckboxDropdown label={t('xSales.fChannel')} options={filterMeta?.channels || []} value={weekly2Mode ? inactiveWeekly2DropdownValue : filters.channels} onChange={(value) => updateFilter('channels', value)} disabled={weekly2Mode} />
            <CheckboxDropdown label={t('xSales.fSubchannel')} options={filterMeta?.channel2s || []} value={weekly2Mode ? inactiveWeekly2DropdownValue : filters.channel2s} onChange={(value) => updateFilter('channel2s', value)} disabled={weekly2Mode} />
            <CheckboxDropdown label={t('xSales.fCustomerName')} options={filterMeta?.customerNames || filterMeta?.customers || []} value={weekly2Mode ? inactiveWeekly2DropdownValue : filters.customerNames} onChange={(value) => updateFilter('customerNames', value)} disabled={weekly2Mode} />
            <CheckboxDropdown label="SKU" options={filterMeta?.skus || []} value={weekly2Mode ? inactiveWeekly2DropdownValue : filters.skus} onChange={(value) => updateFilter('skus', value)} disabled={weekly2Mode} />
            <CheckboxDropdown label={t('xSales.fLine')} options={filterMeta?.lines || []} value={weekly2Mode ? inactiveWeekly2DropdownValue : filters.lines} onChange={(value) => updateFilter('lines', value)} disabled={weekly2Mode} />
            <CheckboxDropdown label={t('xSales.fRegion')} options={filterMeta?.regions || []} value={weekly2Mode ? inactiveWeekly2DropdownValue : filters.regions} onChange={(value) => updateFilter('regions', value)} disabled={weekly2Mode} />
            <CheckboxDropdown label={t('xSales.fSubregion')} options={filterMeta?.subregions || []} value={weekly2Mode ? inactiveWeekly2DropdownValue : filters.subregions} onChange={(value) => updateFilter('subregions', value)} disabled={weekly2Mode} />
            <CheckboxDropdown label={t('xSales.fCountry')} options={filterMeta?.countries || []} value={weekly2Mode ? inactiveWeekly2DropdownValue : filters.countries} onChange={(value) => updateFilter('countries', value)} disabled={weekly2Mode} />

            <div className="srd-actions">
              <button type="button" className="srd-primary" onClick={loadReport} disabled={loadingFilters || loadingReport}>
                {loadingReport ? t('xSales.loading') : t('xSales.refresh')}
              </button>
              <button type="button" className="srd-export" onClick={exportPdf} disabled={!report || loadingReport || exportingPdf || exportingXlsx}>
                {exportingPdf ? t('xSales.exporting') : t('xSales.exportPdf')}
              </button>
              <button type="button" className="srd-export srd-export-xlsx" onClick={exportXlsx} disabled={!report || loadingReport || exportingPdf || exportingXlsx}>
                {exportingXlsx ? t('xSales.exporting') : t('xSales.exportExcel')}
              </button>
              <button type="button" className="srd-clear" onClick={clearFilters} disabled={!hasActiveFilters} title={t('xSales.clearTitle')}>
                {t('xSales.clearFilter')}
              </button>
            </div>

            <label className="srd-check">
              <input type="checkbox" checked={filters.excludeDistributor} onChange={(event) => updateFilter('excludeDistributor', event.target.checked)} />
              {t('xSales.excludeDistributor')}
            </label>
            <label className="srd-check">
              <input type="checkbox" checked={filters.includeOversea} onChange={(event) => updateFilter('includeOversea', event.target.checked)} />
              {t('xSales.includeOversea')}
            </label>
            <label className="srd-check">
              <input type="checkbox" checked={filters.includeInternal} onChange={(event) => updateFilter('includeInternal', event.target.checked)} />
              {t('xSales.includeInternal')}
            </label>
            <label className="srd-check">
              <input type="checkbox" checked={autoRun} onChange={(event) => setAutoRun(event.target.checked)} />
              {t('xSales.autoRun')}
            </label>
            <label className="srd-check srd-visual-toggle">
              <input type="checkbox" checked={showVisualAnalytics} onChange={(event) => setShowVisualAnalytics(event.target.checked)} />
              {t('xSales.showVisuals')}
            </label>
            </>)}
          </div>
        </section>
        {!BP_TABLES_ON && <span hidden data-off={BP_TABLES_OFF_MARK} />}
        {bizPage ? (
          <div className="srd-report-scroll bz-host">
            {filters.reportType === 'inventory' ? <BizInventory /> : filters.reportType === 'pole' ? <BizPole /> : <BizMarketing />}
          </div>
        ) : (<>

        {error && <div className="srd-message error">{error}</div>}
        {loadingFilters && !filterMeta && <div className="srd-message">{t('xSales.loadingFilters')}</div>}

        {(!loadingReport || report) && (
          <div className="srd-x-scrollbar" ref={topScrollRef} onScroll={() => syncHorizontalScroll('top')}>
            <div className="srd-x-scrollbar-inner" style={{ width: `${reportScrollWidth}px` }} />
          </div>
        )}

        <div className="srd-report-scroll" ref={reportScrollRef} onScroll={() => syncHorizontalScroll('bottom')}>
          {loadingReport && !report ? (
            <SalesReportLoading />
          ) : (
            <>
              {loadingReport && (
                <div className="srd-report-loading srd-report-loading--banner" role="status" aria-live="polite">
                  <span className="srd-report-loading-spinner" aria-hidden="true" />
                  <strong>{t('xSales.loading')}</strong>
                </div>
              )}

              <section className="srd-summary">
                <h2>{reportLabel(filters.reportType, t)}</h2>
                <p>{subtitle}</p>
                {report?.fromCache && !loadingReport && <span>{t('xSales.fromCache')}</span>}
                {meta?.monthSnapshotLoading && <span>{t('xSales.updatingSnapshot')}</span>}
                {meta?.monthSnapshotError && <span>{meta.monthSnapshotError}</span>}
              </section>

              {INSIGHTS_ON ? (
                <AiInsightPanel
                  storageKey="aiInsight:salesReport"
                  askKey={report && !loadingReport && insightBody && ['weekly', 'monthly'].includes(insightBody.page) ? JSON.stringify(insightBody) : null}
                  blocked={insightBody && !['weekly', 'monthly'].includes(insightBody.page) ? t('xInsight.onlyWeeklyMonthly') : null}
                  ask={({ refresh, lang }) => {
                    const { page, ...body } = insightBody;
                    return askInsight('/api/insights/sales-report', { lang, refresh, report: salesReportRequestBody(body) });
                  }}
                />
              ) : <span hidden data-off={INSIGHTS_OFF_MARK} />}

              {showVisualAnalytics && filters.reportType !== 'weekly2' && <Phase145VisualAnalytics report={report} meta={meta} filters={filters} visualHistory={visualHistory} loadingVisualHistory={loadingVisualHistory} loadingReport={loadingReport} />}

              {!report && !loadingReport && (
                <div className="srd-empty">{t('xSales.emptyPrompt')}</div>
              )}

              {filters.reportType === 'weekly2' && report ? (
                <WeeklyReport2View report={report} meta={meta} filters={filters} />
              ) : filters.reportType === 'weeklyFlash' && report ? (
                <WeeklyFlashReportView report={report} meta={meta} filters={filters} />
              ) : (
                report?.sections?.map((section) => {
                  let displaySection = section;
                  if (section.key === 'country') {
                    const allRows = section.rows || section.data || [];
                    const dataRows = allRows.filter((r) => String(r?.rowType || '').toLowerCase() !== 'total' && String(r?.dimensionValue || '').toLowerCase() !== 'total');
                    const totalRows = allRows.filter((r) => String(r?.rowType || '').toLowerCase() === 'total' || String(r?.dimensionValue || '').toLowerCase() === 'total');
                    displaySection = { ...section, rows: [...dataRows.slice(0, 10), ...totalRows], topN: 10 };
                  }
                  return (
                    <SalesReportSection
                      key={section.key}
                      section={displaySection}
                      columns={columns}
                      currencySymbol={meta?.currencySymbol || ''}
                    />
                  );
                })
              )}
            </>
          )}
        </div>
        </>)}
      </main>
    </div>
  );
}

export default SalesReportDashboard;
